import { UnitAI } from './UnitAI.js';
import {
  GAME_CONFIG, AI_CONFIG, PROJECTILE_CONFIG, RESOURCE_CONFIG,
  MOVEMENT_CONFIG, COMBAT_CONFIG, THRESHOLD_CONFIG, UI_CONFIG,
  CANVAS_CONFIG, BASE_CONFIG
} from '../config.js';
import { getDistance, clamp, randomRange } from '../utils/math.js';
import { isDebugMode, debugLog, debugWarn, debugError } from '../utils/debug.js';
import { Projectile } from '../entities/Projectile.js';
import { Metal } from '../entities/Metal.js';
import { Corpse } from '../entities/Corpse.js';
import { Nebula } from '../entities/Nebula.js';
import { Crystal } from '../entities/Crystal.js';
import { createSoldierStateMachine, guards, events, createContext } from './SoldierStateMachine.js';
import { interpret } from 'robot3';

/**
 * SoldierAI - Finite state machine based AI for combat units
 *
 * Architecture:
 * - Uses Robot3 finite state machine library for behavioral states
 * - Perception system: scans nearby entities every 100ms
 * - Decision making: evaluates guards and sends events to state machine
 * - State execution: runs current state handler every frame (60fps)
 *
 * Key Systems:
 * - Predictive targeting: Leads moving targets based on skill level
 * - Engagement tracking: Monitors damage dealt/taken for retreat decisions
 * - Resource collection: Metal, nebulas, crystals, corpses
 * - Radio coordination: Responds to ally assist requests
 * - Combat positioning: Strafing, backpedaling, range management
 *
 * States: idle → hunting → fighting ⟷ fleeing
 *         gathering → returning_resource → hunting
 *         defending, assisting, final_hunt (no base)
 *
 * Decision Flow (every 100ms):
 * 1. updatePerception() - Find nearby entities
 * 2. makeDecision() - Evaluate conditions and send events
 * 3. State machine transitions based on guards
 * 4. executeState() runs state handler (every frame)
 */
export class SoldierAI extends UnitAI {
  /**
   * Create AI controller for a soldier
   * @param {Soldier} unit - The soldier unit this AI controls
   */
  constructor(unit) {
    super(unit);
    
    // Combat specific properties
    this.weaponRange = GAME_CONFIG.BASE_WEAPON_RANGE;
    this.fireRate = AI_CONFIG.FIRE_RATE;
    this.lastFireTime = 0;
    
    // Target tracking for prediction
    this.targetPrevX = null;
    this.targetPrevY = null;
    this.targetPrevTime = 0;
    this.targetInitialized = false;
    this.lastPredictedX = 0;
    this.lastPredictedY = 0;
    this.offScreenPredictionCount = 0;
    
    // Skill affects accuracy
    this.skill = randomRange(AI_CONFIG.SKILL_RANGE.min, AI_CONFIG.SKILL_RANGE.max);
    
    // Engagement tracking
    this.engagementStartTime = 0;
    this.engagementDamageDealt = 0;
    this.engagementDamageTaken = 0;
    this.retreatCooldown = 0;
    
    // Combat movement
    this.combatAngle = Math.random() * Math.PI * 2;
    
    // Wandering
    this.wanderAngle = Math.random() * Math.PI * 2;
    
    // Base tracking
    this.lastBaseAlive = unit.base && unit.base.alive;
    
    // Target switching
    this.lastTargetCheck = 0;
    
    // Nebula gathering
    this.targetNebula = null;
    this.nearbyNebulas = [];
    this.gatheringStartTime = 0;
    this.assignedToNebula = false; // Track if assigned by base priority
    
    // Safety check tracking to prevent spam
    this.lastSafetyCheckFrame = 0;
    this.lastSafetyCheckIssue = null;

    // resource collection
    this.resourceBeingCollected = null; // Track resource being collected

    // Assist/radio coordination
    this.radioSystem = unit.getSystem ? unit.getSystem('radio') : null;
    this.assistObjective = null;

    // Initialize state machine
    this.stateMachine = createSoldierStateMachine();
    const initialContext = createContext(unit, this);
    this.stateService = interpret(this.stateMachine, () => {
      // State machine change handler
      this.onStateMachineChange();
    }, initialContext);
    
    // Send initial event to kick start the state machine
    setTimeout(() => {
      this.sendEvent(events.CHECK_CONDITIONS);
    }, 100); // Small delay to ensure everything is initialized
  }

  /**
   * Check if soldier has an active assist objective from radio system
   * @returns {boolean} True if soldier is coordinating with allies
   */
  hasActiveAssist() {
    return !!this.assistObjective;
  }

  /**
   * Update or cancel assist/defense objective from radio system
   *
   * When allies call for help or bases request defense, the radio system
   * assigns objectives. This method handles receiving and updating them.
   *
   * @param {Object|null} objective - Objective details (null to cancel)
   * @param {string} reason - Reason for update ('assigned', 'cancelled', 'expired', etc.)
   */
  handleAssistObjectiveUpdate(objective, reason = 'update') {
    const wasActive = !!this.assistObjective;
    const wasAssisting = this.unit.state === 'assisting';
    const wasDefending = this.unit.state === 'defending_base';

    if (!objective) {
      this.assistObjective = null;

      if (wasActive && wasAssisting) {
        this.sendEvent(events.ASSIST_CANCELLED);
      } else if (wasActive && wasDefending) {
        this.sendEvent(events.DEFENSE_CANCELLED);
      }

      return;
    }

    this.assistObjective = {
      ...objective,
      dispatched: false,
      engaged: false,
      reason,
      assignedAt: Date.now(),
      waitDeadline: null,
      patrolAngle: Math.random() * Math.PI * 2  // Random starting patrol angle for defense
    };

    // Send appropriate event based on objective type
    if (objective.type === 'defend_base' && reason === 'assigned') {
      this.sendEvent(events.BASE_DEFENSE_REQUEST);
    } else if (objective.type === 'assist' && reason === 'assigned') {
      this.sendEvent(events.ALLY_ASSIST_REQUEST);
    }
  }

  shouldRespondToAssist(context) {
    if (!this.assistObjective) return false;

    // Already en route or engaged
    if (this.unit.state === 'assisting' || this.assistObjective.dispatched) {
      return false;
    }

    if (this.assistObjective.expiresAt && Date.now() > this.assistObjective.expiresAt) {
      this.cancelAssistObjective('expired');
      return false;
    }

    // Don't respond if we are in critical condition
    if (guards.lowHealth(context) || guards.needsAmmo(context)) {
      return false;
    }

    if (this.unit.state === 'returning_resource' && this.unit.carryingResource) {
      return false;
    }

    return true;
  }

  cancelAssistObjective(reason = 'cancelled') {
    if (!this.assistObjective) return;

    const wasAssisting = this.unit.state === 'assisting';

    if (this.radioSystem && typeof this.radioSystem.completeAssistObjective === 'function') {
      this.radioSystem.completeAssistObjective(reason);
    }

    this.assistObjective = null;

    if (wasAssisting) {
      this.sendEvent(events.ASSIST_CANCELLED);
    }
  }

  completeAssistObjective(reason = 'completed') {
    if (this.radioSystem && typeof this.radioSystem.completeAssistObjective === 'function') {
      this.radioSystem.completeAssistObjective(reason);
    }

    this.assistObjective = null;
  }

  // Override perception to track nebulas
  updatePerception(units, projectiles, resources, bases) {
    super.updatePerception(units, projectiles, resources, bases);
    
    // Track nebulas specifically for gathering logic
    this.nearbyNebulas = this.nearbyResources.filter(r => r instanceof Nebula && r.active);
    
    // Debug resource detection with shorter format to avoid truncation
    if (this.nearbyResources.length > 0) {
      const resourceStr = this.nearbyResources.map(r => `${r.constructor.name}:${r.active ? 'A' : 'I'}`).join(',');
      debugLog(this.unit.level, {
        action: 'resources_detected_in_perception',
        unitId: this.unit.id,
        team: this.unit.team.name,
        totalResources: this.nearbyResources.length,
        resourceData: resourceStr
      });
    }
  }
  
  /**
   * Initialize soldier-specific state handlers
   *
   * Registers all state handler functions with the state machine.
   * Each state has a dedicated handler that runs every frame while in that state.
   *
   * Combat: hunting, fighting, retreating, fleeing
   * Resources: gathering, gathering_nebula, returning_resource
   * Base: defending, seeking_base, reloading, withdrawing, resupplying
   * Special: idle, collecting_corpse, final_hunt, assisting
   */
  initializeStates() {
    super.initializeStates();
    
    // Combat states
    this.stateHandlers.set('hunting', this.handleHuntingState.bind(this));
    this.stateHandlers.set('fighting', this.handleFightingState.bind(this));
    this.stateHandlers.set('retreating', this.handleRetreatingState.bind(this));
    
    // Resource states
    this.stateHandlers.set('gathering', this.handleGatheringState.bind(this));
    this.stateHandlers.set('gathering_nebula', this.handleGatheringNebulaState.bind(this));
    this.stateHandlers.set('returning_resource', this.handleReturningResourceState.bind(this));
    
    // Base states
    this.stateHandlers.set('defending', this.handleDefendingState.bind(this));
    this.stateHandlers.set('seeking_base', this.handleSeekingBaseState.bind(this));
    this.stateHandlers.set('reloading', this.handleReloadingState.bind(this));
    this.stateHandlers.set('withdrawing', this.handleWithdrawingState.bind(this));
    this.stateHandlers.set('resupplying', this.handleResupplyingState.bind(this));
    
    // Special states
    this.stateHandlers.set('idle', this.handleIdleState.bind(this));
    this.stateHandlers.set('fleeing', this.handleFleeingState.bind(this));
    this.stateHandlers.set('collecting_corpse', this.handleCollectingCorpseState.bind(this));
    this.stateHandlers.set('final_hunt', this.handleFinalHuntState.bind(this));
    this.stateHandlers.set('assisting', this.handleAssistingState.bind(this));
    this.stateHandlers.set('defending_base', this.handleDefendingBaseState.bind(this));
  }
  
  /**
   * Handle state machine transitions
   *
   * Called by Robot3 state machine whenever state changes.
   * Synchronizes the soldier entity's state with the state machine,
   * and logs transitions for debugging.
   */
  onStateMachineChange() {
    const newStateName = this.stateService.machine.current;
    const oldStateName = this.unit.state;
    if (newStateName !== oldStateName) {
      debugLog(this.unit.level, {
        action: 'state_transition',
        unitId: this.unit.id,
        from: oldStateName,
        to: newStateName,
        hasResource: !!this.unit.carryingResource,
        resourceType: this.unit.carryingResource?.type || 'none',
        health: Math.floor(this.unit.health),
        courage: Math.floor(this.unit.courage),
        ammo: this.unit.ammo
      });
      this.unit.setState(newStateName);
    }
  }
  
  /**
   * Send event to state machine to trigger state transitions
   *
   * This is the ONLY way to change states - never call setState() directly!
   * Automatically updates context with current unit/AI state before sending.
   *
   * Common events: ENEMY_FOUND, ENEMY_DEFEATED, LOW_HEALTH, LOW_AMMO,
   *                RESOURCE_FOUND, RESOURCE_COLLECTED, BASE_REACHED, etc.
   *
   * @param {string} eventName - Event name from SoldierStateMachine events object
   */
  sendEvent(eventName) {
    // Clean up repair status when leaving defending state
    const currentState = this.stateService.machine.current;
    if (currentState === 'defending' && this.unit.isRepairingBase && this.unit.base) {
      this.unit.base.removeRepairer(this.unit);
      this.unit.isRepairingBase = false;
    }
    
    // Update context before sending event
    const newContext = createContext(this.unit, this);
    this.stateService.send(eventName, newContext);
  }
  
  /**
   * Make AI decisions based on current conditions
   *
   * Called every 100ms (not every frame) to evaluate game state and send events.
   * This is where the AI "thinks" about what to do next.
   *
   * Decision hierarchy (priority order):
   * 1. Safety checks - fix stuck states (returning without resource, etc.)
   * 2. Base lost/destroyed - transition to final_hunt mode
   * 3. Critical conditions - low health, low ammo, low courage
   * 4. Opportunities - corpses to loot, resources to gather
   * 5. Combat - enemies in range, defending base
   * 6. Radio - respond to ally assist requests
   *
   * Note: State machine guards filter events, so not all sent events cause transitions
   */
  makeDecision() {
    const unit = this.unit;
    const now = Date.now();
    
    // Verify state synchronization
    const stateMachineState = this.stateService.machine.current;
    if (stateMachineState !== unit.state) {
      debugError(unit.level, 'Critical: State mismatch detected!', {
        unitId: unit.id,
        team: unit.team.name,
        unitState: unit.state,
        stateMachineState: stateMachineState
      });
      // This should not happen with proper state machine usage
      // If it does, there's a bug in the state machine integration
    }
    
    // Safety check - if in returning_resource state without resource, fix it
    if (unit.state === 'returning_resource' && !unit.carryingResource) {
      const issueKey = 'returning_without_resource';
      if (this.lastSafetyCheckIssue !== issueKey || now - this.lastSafetyCheckFrame > 1000) {
        debugWarn(unit.level, 'Fixing stuck returning_resource state', {
          unitId: unit.id,
          team: unit.team.name
        });
        this.lastSafetyCheckIssue = issueKey;
        this.lastSafetyCheckFrame = now;
      }
      this.sendEvent(events.CHECK_CONDITIONS);
      return;
    }
    
    // Safety check - if in hunting state with resource, fix it
    if (unit.state === 'hunting' && unit.carryingResource) {
      const issueKey = 'hunting_with_resource';
      if (this.lastSafetyCheckIssue !== issueKey || now - this.lastSafetyCheckFrame > 1000) {
        debugWarn(unit.level, 'Unit in hunting state with resource!', {
          unitId: unit.id,
          team: unit.team.name,
          resource: unit.carryingResource?.type || 'unknown',
          resourceObj: unit.carryingResource
        });
        this.lastSafetyCheckIssue = issueKey;
        this.lastSafetyCheckFrame = now;
      }
      this.sendEvent(events.RESOURCE_COLLECTED);
      return;
    }
    
    // If in returning_resource state, don't check for other events
    // Let the state handler do its job
    if (unit.state === 'returning_resource' && unit.carryingResource) {
      return;
    }
    
    // Update retreat cooldown
    if (this.retreatCooldown > 0) {
      this.retreatCooldown = Math.max(0, this.retreatCooldown - 16); // ~60fps
    }
    
    // Create context for state machine
    const context = createContext(unit, this);
    
    // Update the state service context
    this.stateService.context = context;
    
    // Check various conditions and send events to state machine
    this.checkAndSendEvents(context);
  }
  
  // Check conditions and send appropriate events to state machine
  checkAndSendEvents(context) {
    const unit = this.unit;

    // Expired assist objectives should be cleared
    if (this.assistObjective && this.assistObjective.expiresAt && Date.now() > this.assistObjective.expiresAt) {
      this.cancelAssistObjective('expired');
    }

    // Check for base destruction (highest priority)
    if (unit.base && !unit.base.alive && this.lastBaseAlive) {
      this.sendEvent(events.BASE_DESTROYED);
      this.lastBaseAlive = false;
      return;
    }
    // Track base alive state
    if (unit.base && unit.base.alive) {
      this.lastBaseAlive = true;
    }
    
    // HIGHEST PRIORITY: Check for nearby corpses (free level-up!) - but not if carrying resources
    if (!unit.carryingResource) {
      const nearbyCorpse = this.findNearestCorpse();
      if (nearbyCorpse && unit.getDistance(nearbyCorpse) <= unit.perceptionRange) {
        this.prepareForCorpseCollection();
        this.sendEvent(events.CORPSE_FOUND);
        return;
      }
    }
    
    // Check if carrying metal resources that need to be returned
    if (unit.carryingResource && unit.carryingResource.type === 'metal' && 
        unit.base && unit.base.alive && 
        (unit.state !== 'returning_resource' && unit.state !== 'gathering')) {
      // Soldiers carrying metal should prioritize returning it
      debugLog(unit.level, {
        action: 'force_return_resource',
        unitId: unit.id,
        currentState: unit.state,
        resource: unit.carryingResource.type
      });
      this.sendEvent(events.RESOURCE_COLLECTED);
      return;
    }
    
    // Critical conditions (high priority)
    // Skip ammo check if returning resources (especially organics)
    if (unit.state !== 'returning_resource' && guards.needsAmmo(context)) {
      this.cancelAssistObjective('needs_ammo');
      this.sendEvent(events.AMMO_EMPTY);
      return;
    }

    // Skip health check if returning critical resources
    if (unit.state !== 'returning_resource' && guards.lowHealth(context)) {
      this.cancelAssistObjective('low_health');
      this.sendEvent(events.LOW_HEALTH);
      return;
    }

    // Respond to ally assist requests if appropriate
    if (this.shouldRespondToAssist(context)) {
      this.assistObjective.dispatched = true;
      this.sendEvent(events.ALLY_ASSIST_REQUEST);
      if (this.unit.state !== 'assisting') {
        // Guard may have prevented transition; allow re-evaluation
        this.assistObjective.dispatched = false;
      }
      return;
    }
    
    // Base priority events
    if (guards.basePriorityDefend(context)) {
      this.sendEvent(events.BASE_PRIORITY_DEFEND);
      return;
    }
    
    // Check for get_nearby_metal priority (base has specific target beyond soldier's perception)
    // Trust base's superior perception (160px vs soldier's 80px)
    if (guards.basePriorityGetNearbyMetal(context) && guards.canGatherMetal(context)) {
      // Base can see specific metal that this unit should collect
      if (unit.base.targetResource && unit.base.targetResource.active &&
          unit.base.targetResource instanceof Metal) {
        debugLog(unit.level, {
          action: 'accepting_base_metal_assignment',
          unitId: unit.id,
          team: unit.team.name,
          metalPos: `(${unit.base.targetResource.x.toFixed(0)}, ${unit.base.targetResource.y.toFixed(0)})`,
          distance: unit.getDistance(unit.base.targetResource).toFixed(1),
          basePerception: '160px',
          soldierPerception: '80px'
        });
        // Don't assign to carryingResource yet - just send event
        this.sendEvent(events.METAL_FOUND);
        return;
      }
    }

    // Check for general get_metal priority (soldier searches their own perception)
    if (guards.basePriorityGetMetal(context) && guards.canGatherMetal(context)) {
      const nearestResource = this.findNearestResource();
      if (nearestResource && nearestResource instanceof Metal) {
        // Don't assign to carryingResource yet - just send event
        this.sendEvent(events.METAL_FOUND);
        return;
      }
    }
    
    // Check for get_organics priority (base wants units to gather nebula)
    if (guards.basePriorityGetOrganics(context) && guards.canGatherNebula(context)) {
      // Base has targetResource set to a nebula
      if (unit.base.targetResource && unit.base.targetResource.active && 
          unit.base.targetResource instanceof Nebula) {
        this.targetNebula = unit.base.targetResource;
        this.assignedToNebula = true; // Mark as base-assigned
        this.sendEvent(events.NEBULA_FOUND);
        return;
      }
    }
    
    // Resource delivery
    if (guards.carryingMetal(context)) {
      // Check if at base center for delivery
      if (guards.isAtBaseCenter(context)) {
        this.sendEvent(events.RESOURCE_DELIVERED);
        return;
      }
      // Otherwise continue returning
      return;
    }
    
    // Nebula gathering (TEST: temporarily higher priority to debug)
    if (!unit.carryingResource) {
      const canGatherNebula = guards.canGatherNebula(context);
      const nearestNebula = this.findNearestNebula();
      
      // Simple debug to avoid log truncation
      const allResources = this.nearbyResources.map(r => `${r.type}:${r.active ? 'A' : 'I'}`).join(',');
      const nebulaDetails = this.nearbyResources.filter(r => r.type === 'nebula').map(n => `${n.active ? 'A' : 'I'}`).join(',');
      
      debugLog(unit.level, {
        action: 'nebula_detection_attempt',
        unitId: unit.id,
        team: unit.team.name,
        canGatherNebula: canGatherNebula,
        resourceCount: this.nearbyResources.length,
        resourceTypes: allResources,
        nebulaCount: this.nearbyNebulas.length,
        nebulaStates: nebulaDetails
      });
      
      if (canGatherNebula && nearestNebula && unit.getDistance(nearestNebula) <= unit.perceptionRange) {
        this.targetNebula = nearestNebula;
        
        debugLog(unit.level, {
          action: 'nebula_found_sending_event',
          unitId: unit.id,
          team: unit.team.name,
          nebulaPos: `(${nearestNebula.x.toFixed(0)}, ${nearestNebula.y.toFixed(0)})`,
          distance: unit.getDistance(nearestNebula).toFixed(1),
          nebulaActive: nearestNebula.active,
          nebulaGatherers: nearestNebula.gatherers ? nearestNebula.gatherers.size : 'no gatherers set'
        });
        
        this.sendEvent(events.NEBULA_FOUND);
        return;
      }
    }

    // Combat events
    const target = this.selectBestTarget();
    if (target) {
      this.currentTarget = target;
      unit.target = target; // Store for debug rendering
      this.sendEvent(events.ENEMY_FOUND);
      return;
    } else if (this.currentTarget && !this.isValidTarget(this.currentTarget)) {
      this.currentTarget = null;
      unit.target = null; // Clear debug target
      unit.clearFaceTargetOverride();
      unit.stop();
      if (this.assistObjective && this.assistObjective.engaged) {
        this.completeAssistObjective('target_neutralized');
      }
      if (this.radioSystem && typeof this.radioSystem.resolveOutgoingRequest === 'function') {
        this.radioSystem.resolveOutgoingRequest('target_neutralized');
      }
      this.sendEvent(events.ENEMY_DEFEATED);
      return;
    }
    
    // Retreat check (only in fighting state)
    if (unit.state === 'fighting' && this.shouldRetreatFromCombat()) {
      this.sendEvent(events.SHOULD_RETREAT);
      this.retreatCooldown = THRESHOLD_CONFIG.RETREAT_COOLDOWN_DURATION; // 3 second cooldown
      return;
    }
    
    // Resource gathering - check for crystals first (any level can collect)
    if (!unit.carryingResource) {
      const nearestResource = this.findNearestResource();
      if (nearestResource && unit.getDistance(nearestResource) <= unit.perceptionRange) {
        // Crystal cores can be collected by any level unit
        if (nearestResource instanceof Crystal) {
          this.sendEvent(events.CRYSTAL_FOUND);
          return;
        }
        // Metal has level restrictions
        else if (nearestResource instanceof Metal && guards.canGatherMetal(context)) {
          this.sendEvent(events.METAL_FOUND);
          return;
        }
      }
    }
    
    // Health and ammo restoration checks
    if (guards.healthyEnough(context) && guards.hasEnoughAmmo(context)) {
      this.sendEvent(events.HEALTH_RESTORED);
      return;
    }
    
    // General condition check (for defending -> hunting transitions, etc.)
    this.sendEvent(events.CHECK_CONDITIONS);
  }

  // Reset movement and targeting before transitioning into corpse collection
  prepareForCorpseCollection() {
    const unit = this.unit;

    if (unit && typeof unit.clearFaceTargetOverride === 'function') {
      unit.clearFaceTargetOverride();
    }

    if (unit) {
      if (typeof unit.stop === 'function') {
        unit.stop();
      } else {
        unit.vx = 0;
        unit.vy = 0;
      }
    }

    this.currentTarget = null;
  }

  // State handlers
  handleIdleState() {
    // Idle state - send immediate event to transition to active state
    this.sendEvent(events.CHECK_CONDITIONS);
  }

  /**
   * Handle 'assisting' state - respond to ally assist request
   *
   * Soldier is moving to help a teammate who called for assistance.
   * This creates emergent squad tactics without explicit formations.
   *
   * Behavior:
   * 1. Move towards assist position at slightly faster speed (1.05x)
   * 2. When within arrival radius (48px default):
   *    - Search for enemy in engage radius (160px default)
   *    - Prefer specific enemy ID if provided
   *    - Lock onto enemy and transition to 'fighting'
   * 3. If no enemy found:
   *    - Wait 750ms in case enemy is just out of range
   *    - Cancel if area remains clear
   * 4. Cancel if request expires (6 second TTL)
   *
   * State Transitions:
   * - ASSIST_TARGET_REACHED → 'fighting' (found enemy)
   * - ASSIST_CANCELLED → 'hunting' (no enemy, expired, or interrupted)
   * - LOW_HEALTH → 'withdrawing' (took damage while assisting)
   * - AMMO_EMPTY → 'reloading' (ran out during approach)
   *
   * Integration:
   * - Called every frame while state === 'assisting'
   * - Objective set by RadioSystem.update() when accepting request
   * - RadioSystem handles utility scoring and selection
   */
  handleAssistingState() {
    const unit = this.unit;
    const objective = this.assistObjective;

    // Safety check: objective should exist
    if (!objective) {
      this.sendEvent(events.ASSIST_CANCELLED);
      return;
    }

    // Check if request expired (6 second TTL)
    if (objective.expiresAt && Date.now() > objective.expiresAt) {
      this.completeAssistObjective('expired');
      this.sendEvent(events.ASSIST_CANCELLED);
      return;
    }

    const targetPosition = objective.position;
    const distance = unit.getDistance(targetPosition);

    // Move to assist location at slightly faster speed
    unit.moveTowards(targetPosition.x, targetPosition.y, 1.05);

    // Check if we've arrived at the assist location
    if (distance <= (objective.arrivalRadius ?? 40)) {
      // Search for enemy in engage radius
      const enemy = this.findEnemyNearPosition(targetPosition, objective.engageRadius ?? 160, objective.enemyId);

      if (enemy) {
        // Found the enemy! Lock on and transition to fighting
        this.currentTarget = enemy;
        unit.target = enemy;
        if (this.assistObjective) {
          this.assistObjective.engaged = true;
        }
        this.sendEvent(events.ASSIST_TARGET_REACHED);
        return;
      }

      // No enemy found yet - wait a bit in case they're nearby
      if (!objective.waitDeadline) {
        objective.waitDeadline = Date.now() + 750;  // Wait 750ms
      } else if (Date.now() > objective.waitDeadline) {
        // Area is clear - either ally defeated enemy or enemy fled
        this.completeAssistObjective('assist_area_clear');
        this.sendEvent(events.ASSIST_CANCELLED);
      }
    }
  }

  /**
   * Handle defending_base state - rally to base and patrol for attackers
   *
   * Soldiers responding to base defense requests:
   * 1. Move to base location
   * 2. Patrol around base within patrol radius
   * 3. Engage enemies near the base
   * 4. Cancel defense when request expires or base recovers
   */
  handleDefendingBaseState() {
    const unit = this.unit;
    const objective = this.assistObjective;

    // Safety check: objective should exist and be defense type
    if (!objective || objective.type !== 'defend_base') {
      this.sendEvent(events.DEFENSE_CANCELLED);
      return;
    }

    // Check if request expired (10 second TTL)
    if (objective.expiresAt && Date.now() > objective.expiresAt) {
      this.completeAssistObjective('expired');
      this.sendEvent(events.DEFENSE_CANCELLED);
      return;
    }

    // Check if base is destroyed or recovered
    const defendedBase = this.nearbyBases?.find(b => b.id === objective.baseId && b.alive && b.team.id === unit.team.id);
    if (!defendedBase) {
      this.completeAssistObjective('base_lost');
      this.sendEvent(events.DEFENSE_CANCELLED);
      return;
    }

    // If base has recovered (health > 60%), stand down
    if (defendedBase.health / defendedBase.maxHealth > 0.6) {
      this.completeAssistObjective('base_recovered');
      this.sendEvent(events.DEFENSE_CANCELLED);
      return;
    }

    const basePosition = objective.position;
    const distanceToBase = unit.getDistance(basePosition);
    const patrolRadius = objective.patrolRadius ?? 100;
    const arrivalRadius = objective.arrivalRadius ?? 60;

    // Phase 1: Move to base
    if (distanceToBase > arrivalRadius && !objective.dispatched) {
      unit.moveTowards(basePosition.x, basePosition.y, 1.1); // Slightly faster

      if (distanceToBase <= arrivalRadius) {
        objective.dispatched = true;
        this.sendEvent(events.DEFENSE_TARGET_REACHED);
      }
      return;
    }

    // Phase 2: Patrol and defend
    objective.dispatched = true;

    // Check for enemies near the base
    const enemy = this.findEnemyNearPosition(basePosition, patrolRadius * 1.5);
    if (enemy) {
      // Enemy found! Engage
      this.currentTarget = enemy;
      unit.target = enemy;
      objective.engaged = true;
      this.sendEvent(events.ENEMY_FOUND);
      return;
    }

    // Phase 3: Patrol around base in a circle
    // Increment patrol angle for circular movement
    objective.patrolAngle = (objective.patrolAngle ?? 0) + 0.02;

    const patrolX = basePosition.x + Math.cos(objective.patrolAngle) * patrolRadius;
    const patrolY = basePosition.y + Math.sin(objective.patrolAngle) * patrolRadius;

    // Move toward patrol position
    unit.moveTowards(patrolX, patrolY, 0.7); // Slow patrol speed
  }

  handleHuntingState() {
    const unit = this.unit;
    
    // Safety check - should not be hunting with resources
    if (unit.carryingResource) {
      // debugWarn(unit.level, 'Unit in hunting state with resource!', {
      //   unitId: unit.id,
      //   team: unit.team.name,
      //   resource: unit.carryingResource?.type || 'unknown',
      //   resourceObj: unit.carryingResource
      // });
      // // Return to delivering the resource
      // this.sendEvent(events.RESOURCE_COLLECTED);
      // return;
    }
    
    // Priority 1: Check for base threats
    if (unit.base && unit.base.alive && this.isBaseUnderAttack()) {
      this.sendEvent(events.BASE_PRIORITY_DEFEND);
      return;
    }
    
    // Priority 2: Look for enemies
    const target = this.selectBestTarget();
    if (target) {
      this.currentTarget = target;
      this.sendEvent(events.ENEMY_FOUND);
      return;
    }
    
    // Priority 3: Check for resources
    if (unit.base ) {
      const nearestResource = this.findNearestResource();
      if (nearestResource && unit.getDistance(nearestResource) <= unit.perceptionRange) {
        // Crystal cores are high priority - any unit can collect
        if (nearestResource instanceof Crystal) {
          this.sendEvent(events.CRYSTAL_FOUND);
          return;
        }
        
        // Metal collection - opportunistic when cargo not full
        if (nearestResource instanceof Metal) {
          // Level < 5 soldiers always collect when cargo has space
          if (unit.level < 5 && unit.getCargoCount() < BASE_CONFIG.SOLDIER_CARGO_CAPACITY) {
            this.sendEvent(events.METAL_FOUND);
            return;
          }

          // Level 5+ only collect if base critically needs ammo (handled by canGatherMetal guard)
          if (unit.level >= 5 && unit.base.ammo < unit.base.maxAmmo * 0.2) {
            this.sendEvent(events.METAL_FOUND);
            return;
          }
        }
      }
    }

    // Check if cargo is full - transition to returning_resource
    if(unit.getCargoCount() >= BASE_CONFIG.SOLDIER_CARGO_CAPACITY){
      unit.carryingResource = { type: 'metal', amount: unit.getCargoCount() };
      this.sendEvent(events.CARGO_FULL);
      return;
    }

    // Default: Wander while hunting
    this.wander();
  }
  
  handleFightingState() {
    const unit = this.unit;
    
    // Only release nebula gathering slot if not assigned by base
    if (!this.assignedToNebula) {
      this.releaseNebulaSlot();
    }
    
    // Check if we just killed an enemy and should look for their corpse
    if (this.justKilledEnemy && this.killedEnemyPosition) {
      this.justKilledEnemy = false;
      
      // Look for a corpse near the kill location
      const corpseNearKill = this.nearbyResources.find(res => 
        res instanceof Corpse && 
        res.active && 
        unit.getDistance(res) < THRESHOLD_CONFIG.CORPSE_NEAR_KILL_DISTANCE
      );
      
      if (corpseNearKill) {
        // Immediately go for the corpse
        this.prepareForCorpseCollection();
        this.sendEvent(events.CORPSE_FOUND);
        return;
      }
      
      this.killedEnemyPosition = null;
    }
    
    // Check if target is still valid
    if (!this.isValidTarget(this.currentTarget)) {
      unit.clearFaceTargetOverride();
      unit.stop();
      this.currentTarget = null;

      // PRIORITY 1: Check if we're carrying resources that need delivery
      if (unit.carryingResource) {
        debugLog(unit.level, {
          action: 'returning_to_deliver_after_combat',
          unitId: unit.id,
          team: unit.team.name,
          resourceType: unit.carryingResource.type,
          amount: unit.carryingResource.amount
        });
        this.sendEvent(events.RESOURCE_COLLECTED);
        return;
      }
      
      // PRIORITY 2: Check if we should return to assigned nebula gathering
      if (this.assignedToNebula && this.targetNebula && this.targetNebula.active &&
          this.targetNebula.gatherers.size < RESOURCE_CONFIG.NEBULA_MAX_GATHERERS) {
        debugLog(unit.level, {
          action: 'returning_to_assigned_nebula',
          unitId: unit.id,
          team: unit.team.name
        });
        this.sendEvent(events.NEBULA_FOUND);
        return;
      }
      
      this.sendEvent(events.ENEMY_DEFEATED);
      return;
    }
    
    // Periodically check for better targets
    const now = Date.now();
    if (now - this.lastTargetCheck > AI_CONFIG.TARGET_CHECK_INTERVAL) {
      this.lastTargetCheck = now;
      
      const currentDistance = unit.getDistance(this.currentTarget);
      let betterTarget = null;
      let betterDistance = currentDistance;
      
      // Check all nearby enemies for a better target
      for (const enemy of this.nearbyEnemies) {
        const distance = unit.getDistance(enemy);
        // Switch if enemy is significantly closer
        if (distance < currentDistance * AI_CONFIG.TARGET_SWITCH_THRESHOLD && 
            distance < this.weaponRange * COMBAT_CONFIG.TARGET_SWITCH_RANGE) {
          betterTarget = enemy;
          betterDistance = distance;
        }
      }
      
      if (betterTarget) {
        debugLog(unit.level, {
          action: 'switching_target',
          oldTarget: this.currentTarget.id,
          newTarget: betterTarget.id,
          oldDistance: currentDistance.toFixed(1),
          newDistance: betterDistance.toFixed(1)
        });
        
        // Switch to better target
        this.currentTarget = betterTarget;
        this.targetInitialized = false; // Reset targeting
      }
    }
    
    const distance = unit.getDistance(this.currentTarget);
    const minDistance = this.weaponRange * COMBAT_CONFIG.COMBAT_MIN_DISTANCE;
    const maxDistance = this.weaponRange * COMBAT_CONFIG.COMBAT_MAX_DISTANCE;
    const faceReleaseDistance = minDistance * 1.05; // Small buffer to prevent rapid toggling

    // Maintain combat distance
    if (distance < minDistance) {
      unit.setFaceTargetOverride(this.currentTarget, { mode: 'backpedal' });
      // Too close, back up with base avoidance
      const dx = unit.x - this.currentTarget.x;
      const dy = unit.y - this.currentTarget.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const fleeDistance = minDistance * 1.5;
      const fleeX = dist > 0 ? this.currentTarget.x + (dx / dist) * fleeDistance : unit.x;
      const fleeY = dist > 0 ? this.currentTarget.y + (dy / dist) * fleeDistance : unit.y;
      unit.moveTowardsWithBaseAvoidance(fleeX, fleeY, unit.allBases || [], MOVEMENT_CONFIG.WANDER_SPEED_MULTIPLIER);
    } else if (distance > maxDistance) {
      unit.clearFaceTargetOverride(this.currentTarget);
      // Too far, move closer but stop at optimal engagement range
      this.moveToOptimalRange(this.currentTarget);
    } else {
      if (distance >= faceReleaseDistance) {
        unit.clearFaceTargetOverride(this.currentTarget);
      }
      // Good distance, strafe
      this.combatStrafe();
    }

    // Try to fire
    const hasFaceOverride = unit.hasFaceTargetOverride();
    let facingAligned = true;

    if (hasFaceOverride) {
      const dx = this.currentTarget.x - unit.x;
      const dy = this.currentTarget.y - unit.y;
      const desiredAngle = Math.atan2(dy, dx);
      let angleDiff = desiredAngle - unit.facingAngle;
      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

      // Require the soldier to largely finish turning back toward the target
      facingAligned = Math.abs(angleDiff) < Math.PI / 18; // ~10 degrees
    }

    if ((!hasFaceOverride || facingAligned) && this.canFireAt(this.currentTarget)) {
      this.fireAt(this.currentTarget);
    }
  }
  
  handleRetreatingState() {
    const unit = this.unit;
    
    // Release nebula gathering slot if we were gathering
    this.releaseNebulaSlot();
    
    // Find all nearby threats
    const threats = this.nearbyEnemies.filter(enemy => 
      enemy.alive && 
      unit.getDistance(enemy) < unit.weaponRange * 2
    );
    
    // If no immediate threats, transition back to hunting
    if (threats.length === 0) {
      unit.clearFaceTargetOverride();
      unit.stop();
      // Check if we can collect corpses while retreating for healing
      if (unit.health < unit.maxHealth * THRESHOLD_CONFIG.HEALTH_CORPSE_SEEK_THRESHOLD) {
        const nearestCorpse = this.findNearestCorpse();
        if (nearestCorpse && unit.getDistance(nearestCorpse) < AI_CONFIG.CORPSE_COLLECTION_RADIUS * THRESHOLD_CONFIG.CORPSE_FLEE_COLLECTION_RANGE) {
          unit.moveTowardsWithBaseAvoidance(nearestCorpse.x, nearestCorpse.y, unit.allBases || [], MOVEMENT_CONFIG.URGENT_SPEED_MULTIPLIER);
          return;
        }
      }
      
      this.sendEvent(events.RETREAT_COMPLETE);
      return;
    }
    
    // Move away from threats
    let retreatX = 0;
    let retreatY = 0;
    
    for (let threat of threats) {
      const dx = unit.x - threat.x;
      const dy = unit.y - threat.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance > 0) {
        // Weight by threat level and distance
        const threatWeight = (threat.level / unit.level) * (1 / distance);
        retreatX += (dx / distance) * threatWeight;
        retreatY += (dy / distance) * threatWeight;
      }
    }
    
    // Normalize retreat direction and set controlled retreat speed
    const retreatDistance = Math.sqrt(retreatX * retreatX + retreatY * retreatY);
    if (retreatDistance > 0) {
      const retreatSpeed = MOVEMENT_CONFIG.FLEE_SPEED_MULTIPLIER; // 50% faster than base speed
      const desiredVx = (retreatX / retreatDistance) * retreatSpeed;
      const desiredVy = (retreatY / retreatDistance) * retreatSpeed;
      unit.moveTowards(unit.x + desiredVx * 10, unit.y + desiredVy * 10, retreatSpeed);
    }
    
    // Opportunistic firing while retreating (if we have ammo and are in range)
    const now = Date.now();
    if (unit.ammo > 0 && this.currentTarget && 
        unit.getDistance(this.currentTarget) <= unit.weaponRange) {
      if (now - this.lastFireTime > unit.fireRate * COMBAT_CONFIG.SLOW_FIRE_MULTIPLIER) { // Slower firing while retreating
        this.fireAt(this.currentTarget);
      }
    }
  }
  
  handleGatheringState() {
    const unit = this.unit;

    // Find resource to collect - prioritize base's target if it exists
    let targetResource = null;
    
    // Check if base has a specific target resource
    if (unit.base && unit.base.priority === 'get_nearby_metal' && 
        unit.base.targetResource && unit.base.targetResource.active) {
      targetResource = unit.base.targetResource;
    } else {
      // Otherwise find nearest resource
      targetResource = this.findNearestResource();
    }
    
    if (!targetResource || !targetResource.active) {
      // No valid resource found - return to hunting
      debugLog(unit.level, {
        action: 'gathering_no_target_return_to_hunting',
        unitId: unit.id,
        team: unit.team.name,
        nearbyResourceCount: this.nearbyResources.length
      });
      this.sendEvent(events.CHECK_CONDITIONS); // Transition back to hunting
      return;
    }
    
    const distance = unit.getDistance(targetResource);

    // Safety check - if resource is too far, wander toward it
    if (distance > unit.perceptionRange) {
      debugLog(unit.level, {
        action: 'gathering_resource_too_far_wandering',
        unitId: unit.id,
        team: unit.team.name,
        distance: distance.toFixed(1),
        perceptionRange: unit.perceptionRange
      });
      // Wander in the general direction of the distant resource
      unit.moveTowards(targetResource.x, targetResource.y, MOVEMENT_CONFIG.RESOURCE_APPROACH_SPEED); // Slow approach
      return;
    }

    // Move towards resource until within collection range
    if (distance > AI_CONFIG.CORPSE_COLLECTION_RADIUS) {
      unit.moveTowards(targetResource.x, targetResource.y);
      return;
    }

    // Check if animation is already in progress (BEFORE checking canBeCollectedBy, which returns false when collecting)
    if (targetResource.isBeingCollected) {
      // Check if collection animation is complete
      if (targetResource.isFullyCollected && targetResource.isFullyCollected()) {
        // console.log(`[GATHERING] ${unit.team.name} soldier ${unit.id}: Collection animation complete`);
        const result = targetResource.collect(unit);
        targetResource.active = false;

        // Handle different resource types
        if (targetResource instanceof Corpse || targetResource instanceof Crystal) {
          this.sendEvent(events.RESOURCE_COLLECTED);
          return;
        }

        // Use cargo system for metal
        if (targetResource instanceof Metal) {
          // console.log(`[GATHERING] ${unit.team.name} soldier ${unit.id}: Adding metal to cargo`);
          unit.addMetalToCargo(result);

          // Check if cargo is full or should deliver
          if (unit.isCargoFull()) {
            unit.carryingResource = { type: 'metal', amount: unit.getCargoCount() };
            this.logResourceGathering('metal', unit.getCargoCount());
            this.sendEvent(events.RESOURCE_COLLECTED);
          } else {
            // Continue gathering - go back to hunting to find more
            this.sendEvent(events.CHECK_CONDITIONS);
          }
          return;
        }

        // Other resources use old system
        unit.carryingResource = result;
        this.logResourceGathering(result.type, result.amount || 1);
        this.sendEvent(events.RESOURCE_COLLECTED);
        return;
      }

      // Animation still in progress - stay near the resource
      unit.moveTowards(targetResource.x, targetResource.y);
      return;
    }

    // Not being collected yet - check if we can start collection
    if (targetResource.canBeCollectedBy && targetResource.canBeCollectedBy(unit)) {
      // console.log(`[GATHERING] ${unit.team.name} soldier ${unit.id}: Starting collection of ${targetResource.type}`);
      targetResource.startCollection(unit);
      return;
    }

    // Can't collect - wander
    if (distance <= AI_CONFIG.CORPSE_COLLECTION_RADIUS) {
      // Can't collect and we're close - wander to find other resources
      debugLog(unit.level, {
        action: 'gathering_cant_collect_wandering',
        unitId: unit.id,
        team: unit.team.name,
        resourceType: targetResource.type,
        distance: distance.toFixed(1)
      });
      this.wander(MOVEMENT_CONFIG.WANDER_SPEED_MULTIPLIER); // Wander at reduced speed to search for other resources
    }
  }
  
  handleGatheringNebulaState() {
    const unit = this.unit;
    
    // Check if base still wants us to gather (for assigned gatherers)
    if (this.assignedToNebula && unit.base && unit.base.priority !== 'get_organics') {
      // Base changed priority, abandon gathering
      debugLog(unit.level, {
        action: 'nebula_assignment_cancelled',
        unitId: unit.id,
        team: unit.team.name,
        reason: 'Base priority changed'
      });
      this.releaseNebulaSlot();
      this.sendEvent(events.NEBULA_INTERRUPTED);
      return;
    }
    
    // If base has get_organics priority and a target nebula, use that
    if (unit.base && unit.base.priority === 'get_organics' && 
        unit.base.targetResource && unit.base.targetResource instanceof Nebula &&
        unit.base.targetResource.active) {
      // Override with base's target nebula
      this.targetNebula = unit.base.targetResource;
    }
    
    // Check if we have a valid nebula target
    if (!this.targetNebula || !this.targetNebula.active) {
      debugLog(unit.level, {
        action: 'nebula_gathering_lost_target',
        unitId: unit.id,
        team: unit.team.name
      });
      this.sendEvent(events.NEBULA_INTERRUPTED);
      return;
    }
    
    const distance = unit.getDistance(this.targetNebula);
    
    // Validate we still have our slot
    if (this.targetNebula.gatherers.has(unit) && distance > RESOURCE_CONFIG.NEBULA_RADIUS + 5) {
      // We've moved too far away - release slot
      debugLog(unit.level, {
        action: 'nebula_slot_released_too_far',
        unitId: unit.id,
        team: unit.team.name,
        distance: distance.toFixed(1)
      });
      this.releaseNebulaSlot();
      this.sendEvent(events.NEBULA_INTERRUPTED);
      return;
    }
    
    // If we're within gathering range, start gathering
    if (distance <= RESOURCE_CONFIG.NEBULA_RADIUS) {
      // Try to claim a gathering slot if we don't have one
      if (!this.targetNebula.gatherers.has(unit)) {
        if (this.targetNebula.gatherers.size < RESOURCE_CONFIG.NEBULA_MAX_GATHERERS) {
          // Claim the slot
          this.targetNebula.gatherers.add(unit);
          unit.carryingResource = { type: 'organics', amount: 0 }; // Start collecting organics
          this.gatheringStartTime = Date.now();
          
          debugLog(unit.level, {
            action: 'nebula_slot_claimed',
            unitId: unit.id,
            team: unit.team.name,
            gatherersCount: this.targetNebula.gatherers.size,
            maxGatherers: RESOURCE_CONFIG.NEBULA_MAX_GATHERERS
          });
          
          this.sendEvent(events.NEBULA_SLOT_CLAIMED);
        } else {
          // Nebula is full - find another target
          debugLog(unit.level, {
            action: 'nebula_full_seeking_other',
            unitId: unit.id,
            team: unit.team.name
          });
          this.targetNebula = null;
          this.sendEvent(events.NEBULA_INTERRUPTED);
          return;
        }
      }
      
      // We're gathering - accumulate organics
      if (unit.carryingResource && unit.carryingResource.type === 'organics') {
        // Use the nebula's gatherFrom method which handles depletion
        const delta = 16.67; // Approximate frame time
        const gatherAmount = this.targetNebula.gatherFrom(unit, delta);
        unit.carryingResource.amount += gatherAmount;
        
        // Check if nebula was depleted
        if (gatherAmount === 0 && this.targetNebula.isDepleted) {
          debugLog(unit.level, {
            action: 'nebula_depleted',
            unitId: unit.id,
            team: unit.team.name,
            organicsCarried: unit.carryingResource.amount.toFixed(1)
          });
          this.releaseNebulaSlot();
          this.sendEvent(events.NEBULA_INTERRUPTED);
          return;
        }
        
        // Check if we have enough organics to deliver (half a unit's worth)
        if (unit.carryingResource.amount >= RESOURCE_CONFIG.ORGANICS_PER_UNIT / 2) {
          debugLog(unit.level, {
            action: 'nebula_organics_collected',
            unitId: unit.id,
            team: unit.team.name,
            amount: unit.carryingResource.amount.toFixed(1)
          });
          
          // Release slot and go deliver
          this.releaseNebulaSlot();
          this.sendEvent(events.RESOURCE_COLLECTED);
          return;
        }
      }
      
      // Reduce movement to stay in position
      unit.vx *= MOVEMENT_CONFIG.BOIDS_MOVEMENT_REDUCTION;
      unit.vy *= MOVEMENT_CONFIG.BOIDS_MOVEMENT_REDUCTION;
      
      // Apply slight separation from other gatherers while staying in range
      this.applySeparationForceFromGatherers();
      
    } else {
      // Move towards the nebula
      unit.moveTowards(this.targetNebula.x, this.targetNebula.y, MOVEMENT_CONFIG.WANDER_SPEED_MULTIPLIER);
      
      // If we had a slot but moved too far, release it (unless assigned by base)
      if (this.targetNebula.gatherers.has(unit) && !this.assignedToNebula) {
        this.releaseNebulaSlot();
      }
    }
  }
  
  // Helper method to apply separation from other gatherers
  applySeparationForceFromGatherers() {
    const unit = this.unit;
    if (!this.targetNebula || !this.targetNebula.gatherers) return;
    
    let separationX = 0;
    let separationY = 0;
    let nearbyCount = 0;
    const separationRadius = MOVEMENT_CONFIG.GATHERER_SEPARATION_RADIUS; // Min distance between gatherers
    
    for (const gatherer of this.targetNebula.gatherers) {
      if (gatherer === unit || !gatherer.alive) continue;
      
      const distance = unit.getDistance(gatherer);
      if (distance < separationRadius && distance > 0) {
        const repulsionStrength = (separationRadius - distance) / separationRadius;
        const dx = unit.x - gatherer.x;
        const dy = unit.y - gatherer.y;
        
        const magnitude = Math.sqrt(dx * dx + dy * dy);
        if (magnitude > 0) {
          separationX += (dx / magnitude) * repulsionStrength;
          separationY += (dy / magnitude) * repulsionStrength;
          nearbyCount++;
        }
      }
    }
    
    if (nearbyCount > 0) {
      separationX /= nearbyCount;
      separationY /= nearbyCount;
      
      // Apply gentle separation force
      unit.vx += separationX * MOVEMENT_CONFIG.GATHERER_SEPARATION_FORCE;
      unit.vy += separationY * MOVEMENT_CONFIG.GATHERER_SEPARATION_FORCE;
    }
  }
  
  // Release nebula gathering slot
  releaseNebulaSlot() {
    const unit = this.unit;
    if (this.targetNebula && this.targetNebula.gatherers && this.targetNebula.gatherers.has(unit)) {
      this.targetNebula.gatherers.delete(unit);
      // Don't clear carryingResource here - the unit might be carrying organics to deliver!
      
      debugLog(unit.level, {
        action: 'nebula_slot_released',
        unitId: unit.id,
        team: unit.team.name,
        remainingGatherers: this.targetNebula.gatherers.size,
        stillCarrying: unit.carryingResource ? unit.carryingResource.type : 'none'
      });
    }
    
    // Clear assignment when slot is released
    this.assignedToNebula = false;
  }
  
  handleReturningResourceState() {
    const unit = this.unit;
    
    debugLog(unit.level, {
      action: 'handleReturningResourceState_called',
      unitId: unit.id,
      team: unit.team.name,
      carryingResource: unit.carryingResource ? unit.carryingResource.type : 'none',
      baseAlive: unit.base ? unit.base.alive : 'no base',
      position: `${unit.x.toFixed(0)},${unit.y.toFixed(0)}`,
      velocity: `${unit.vx.toFixed(2)},${unit.vy.toFixed(2)}`
    });
    
    if (!unit.carryingResource) {
      debugWarn(unit.level, 'Unit in returning_resource state without resource!', {
        unitId: unit.id,
        team: unit.team.name,
        state: unit.state
      });
      this.sendEvent(events.CHECK_CONDITIONS);
      return;
    }
    
    if (!unit.base || !unit.base.alive) {
      // Drop resource if no base
      unit.carryingResource = null;
      this.sendEvent(events.BASE_DESTROYED);
      return;
    }
    
    // Check distance to base
    const baseDistance = unit.getDistance(unit.base);
    
    // If inside base, use boids movement to reach center
    if (baseDistance < unit.base.size) {
      // Check if at center of base to deliver
      if (unit.base.isAtCenter && unit.base.isAtCenter(unit)) {
        // Deposit resource
        if (unit.base.depositResource) {
          const resourceType = unit.carryingResource.type;
          const resourceAmount = unit.carryingResource.amount;
          
          unit.base.depositResource(resourceType, resourceAmount);
          debugLog('Resource', {
            team: unit.team.name,
            unitId: unit.id,
            resource: resourceType,
            amount: resourceAmount,
            delivered: true,
            action: 'depositing',
            cargoDelivered: unit.getCargoCount()
          });
          
          // Clear resource and cargo BEFORE sending event
          // console.log(`[CARGO CLEAR] ${unit.team.name} soldier ${unit.id} delivering ${unit.getCargoCount()} metal`);
          unit.carryingResource = null;
          unit.metalCargo = [];
          
          debugLog('Resource', {
            team: unit.team.name,
            unitId: unit.id,
            action: 'cleared_resource',
            nextEvent: 'RESOURCE_DELIVERED'
          });
          
          // Send resource delivered event - state machine will decide next state
          this.sendEvent(events.RESOURCE_DELIVERED);
          
          debugLog('Resource', {
            team: unit.team.name,
            unitId: unit.id,
            action: 'forced_state_after_delivery',
            newState: unit.state
          });
        } else {
          debugError('No depositResource method on base!', {
            unitId: unit.id,
            team: unit.team.name
          });
        }
        return;
      }
      
      // Use boids-style movement to reach center
      const dx = unit.base.x - unit.x;
      const dy = unit.base.y - unit.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      if (dist > 0) {
        // Strong attraction to center when carrying resources
        unit.vx = (dx / dist) * unit.speed * 0.8;
        unit.vy = (dy / dist) * unit.speed * 0.8;
      }
    } else {
      // Outside base - move towards it (slower when carrying)
      debugLog(unit.level, {
        action: 'returning_resource_movement',
        unitId: unit.id,
        team: unit.team.name,
        baseDistance: baseDistance.toFixed(0),
        basePos: `${unit.base.x.toFixed(0)},${unit.base.y.toFixed(0)}`,
        unitPos: `${unit.x.toFixed(0)},${unit.y.toFixed(0)}`,
        velocityBefore: `${unit.vx.toFixed(2)},${unit.vy.toFixed(2)}`
      });
      
      unit.moveTowards(unit.base.x, unit.base.y, MOVEMENT_CONFIG.RETURN_RESOURCE_SPEED);
      
      debugLog(unit.level, {
        action: 'returning_resource_after_move',
        unitId: unit.id,
        velocityAfter: `${unit.vx.toFixed(2)},${unit.vy.toFixed(2)}`
      });
    }
  }
  
  handleDefendingState() {
    const unit = this.unit;
    
    if (!unit.base || !unit.base.alive) {
      this.sendEvent(events.BASE_DESTROYED);
      return;
    }
    
    // Check if base is still prioritizing defense
    if (unit.base.priority !== 'defend') {
      debugLog(unit.level, {
        action: 'defending_priority_changed',
        unitId: unit.id,
        team: unit.team.name,
        newPriority: unit.base.priority
      });
      this.sendEvent(events.CHECK_CONDITIONS);
      return;
    }
    
    // Find nearest enemy to base
    let nearestThreat = null;
    let nearestDistance = Infinity;
    
    for (const enemy of this.nearbyEnemies) {
      const distToBase = getDistance(enemy, unit.base);
      if (distToBase < nearestDistance && distToBase < THRESHOLD_CONFIG.BASE_DEFEND_RANGE) {
        nearestThreat = enemy;
        nearestDistance = distToBase;
      }
    }
    
    if (nearestThreat) {
      this.currentTarget = nearestThreat;
      const distance = unit.getDistance(nearestThreat);
      
      if (distance > this.weaponRange) {
        unit.moveTowards(nearestThreat.x, nearestThreat.y);
      } else if (this.canFireAt(nearestThreat)) {
        this.fireAt(nearestThreat);
      }
    } else {
      // No threats visible - check if base needs repair
      if (unit.base.needsRepairers && unit.base.needsRepairers()) {
        // Move to base center to repair
        const baseDistance = unit.getDistance(unit.base);
        
        if (baseDistance > BASE_CONFIG.REPAIR_RANGE) {
          // Move to repair range
          unit.moveTowards(unit.base.x, unit.base.y);
          
          debugLog(unit.level, {
            action: 'defending_moving_to_repair',
            unitId: unit.id,
            team: unit.team.name,
            baseHealth: Math.floor(unit.base.health),
            baseMaxHealth: unit.base.maxHealth,
            activeRepairers: unit.base.activeRepairers.size
          });
        } else {
          // In repair range - register as repairer
          if (!unit.isRepairingBase && unit.base.addRepairer(unit)) {
            unit.isRepairingBase = true;
            
            debugLog(unit.level, {
              action: 'defending_started_repair',
              unitId: unit.id,
              team: unit.team.name,
              baseHealth: Math.floor(unit.base.health),
              activeRepairers: unit.base.activeRepairers.size
            });
          }
          
          // Stay near base center while repairing
          if (baseDistance > BASE_CONFIG.CENTER_THRESHOLD) {
            unit.moveTowards(unit.base.x, unit.base.y, MOVEMENT_CONFIG.WANDER_SPEED_MULTIPLIER);
          } else {
            // Apply slight wander to avoid stacking
            this.wanderSlightly();
          }
        }
      } else {
        // Base doesn't need repair or is at capacity - patrol
        if (unit.isRepairingBase && unit.base) {
          unit.base.removeRepairer(unit);
          unit.isRepairingBase = false;
        }
        
        debugLog(unit.level, {
          action: 'defending_patrol',
          unitId: unit.id,
          team: unit.team.name,
          baseDistance: unit.getDistance(unit.base).toFixed(1)
        });
        
        const baseDistance = unit.getDistance(unit.base);
        const idealPatrolDistance = unit.base.size * THRESHOLD_CONFIG.PATROL_IDEAL_DISTANCE; // Patrol at 2x base radius
        
        if (baseDistance > idealPatrolDistance * THRESHOLD_CONFIG.PATROL_MAX_DISTANCE) {
          // Too far from base - move back toward it
          unit.moveTowards(unit.base.x, unit.base.y, MOVEMENT_CONFIG.WANDER_SPEED_MULTIPLIER);
        } else if (baseDistance < idealPatrolDistance * THRESHOLD_CONFIG.PATROL_MIN_DISTANCE) {
          // Too close to base - move away slightly while patrolling
          unit.moveAway(unit.base.x, unit.base.y, MOVEMENT_CONFIG.RETURN_RESOURCE_SPEED);
        } else {
          // Good distance - patrol around base
          this.patrolAroundBase();
        }
      }
    }
  }
  
  handleSeekingBaseState() {
    const unit = this.unit;
    
    if (!unit.base || !unit.base.alive) {
      // Find nearest allied base
      const alliedBases = this.nearbyBases.filter(b => b.alive && unit.isAlly(b));
      if (alliedBases.length > 0) {
        unit.base = alliedBases[0];
      } else {
        this.sendEvent(events.CHECK_CONDITIONS);
        return;
      }
    }
    
    // Check if inside base radius
    if (unit.base.isInside && unit.base.isInside(unit)) {
      this.sendEvent(events.REACHED_BASE);
    } else {
      unit.moveTowards(unit.base.x, unit.base.y, MOVEMENT_CONFIG.QUICK_SPEED_MULTIPLIER); // Move quickly to base
    }
  }
  
  handleWithdrawingState() {
    const unit = this.unit;
    
    // High-priority behavior for critically wounded units (original logic)
    if (!unit.base || !unit.base.alive) {
      // No base available - switch to fleeing instead  
      this.sendEvent(events.BASE_DESTROYED);
      return;
    }
    
    // CRITICAL: Check for nearby corpses while withdrawing!
    // Corpses provide instant level-up which gives:
    // - Full health restore
    // - Full ammo restore 
    // - +25 bonus ammo
    // - All stat increases
    // This is better than reaching base!
    const nearbyCorpse = this.findNearestCorpse();
    if (nearbyCorpse && unit.getDistance(nearbyCorpse) <= unit.perceptionRange) {
      debugLog(unit.level, {
        action: 'withdrawing_found_corpse',
        unitId: unit.id,
        team: unit.team.name,
        currentHealth: Math.floor(unit.health),
        maxHealth: Math.floor(unit.maxHealth),
        distanceToCorpse: Math.floor(unit.getDistance(nearbyCorpse)),
        distanceToBase: unit.base ? Math.floor(unit.getDistance(unit.base)) : 'no base'
      });

      this.prepareForCorpseCollection();
      this.sendEvent(events.CORPSE_FOUND);
      return;
    }
    
    // Check if we've reached base center
    if (unit.base.isAtCenter && unit.base.isAtCenter(unit)) {
      // Already at base - send event to ensure proper state
      this.sendEvent(events.REACHED_BASE);
    } else {
      // Move towards base with urgency, using smart base avoidance (original behavior)
      unit.moveTowardsWithBaseAvoidance(unit.base.x, unit.base.y, unit.allBases || [], MOVEMENT_CONFIG.URGENT_SPEED_MULTIPLIER);
    }
  }
  
  handleReloadingState() {
    const unit = this.unit;
    
    if (!unit.base || !unit.base.alive) {
      this.sendEvent(events.BASE_DESTROYED);
      return;
    }
    
    // Check for nearby corpses - they provide instant full ammo + level up!
    const nearbyCorpse = this.findNearestCorpse();
    if (nearbyCorpse && unit.getDistance(nearbyCorpse) <= unit.perceptionRange) {
      debugLog(unit.level, {
        action: 'reloading_found_corpse',
        unitId: unit.id,
        team: unit.team.name,
        currentAmmo: unit.ammo,
        maxAmmo: unit.maxAmmo,
        distanceToCorpse: Math.floor(unit.getDistance(nearbyCorpse))
      });

      this.prepareForCorpseCollection();
      this.sendEvent(events.CORPSE_FOUND);
      return;
    }
    
    // Move toward base center if not there yet
    if (!unit.base.isAtCenter(unit)) {
      unit.moveTowards(unit.base.x, unit.base.y);
    } else {
      // At center, stop moving (healing happens in Soldier.js)
      unit.vx = 0;
      unit.vy = 0;
      
      // Check if ready to leave base
      const healthReady = unit.health >= unit.maxHealth * THRESHOLD_CONFIG.HEALTH_READY_THRESHOLD;
      const courageReady = unit.courage >= unit.maxCourage * THRESHOLD_CONFIG.COURAGE_READY_THRESHOLD;
      const ammoReady = unit.ammo >= unit.maxAmmo * THRESHOLD_CONFIG.AMMO_READY_THRESHOLD;
      
      if (healthReady && courageReady && ammoReady) {
        // Send event to state machine instead of setting state directly
        this.sendEvent(events.HEALTH_RESTORED);
      }
    }
  }
  
  handleResupplyingState() {
    const unit = this.unit;
    
    if (!unit.base || !unit.base.alive) {
      this.sendEvent(events.BASE_DESTROYED);
      return;
    }
    
    // Use boids-style movement toward base center while maintaining separation
    const baseDistance = unit.getDistance(unit.base);
    if (baseDistance < unit.base.size) {
      // Inside base radius - apply boids-style movement toward center
      const dx = unit.base.x - unit.x;
      const dy = unit.base.y - unit.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      if (dist > THRESHOLD_CONFIG.BASE_CENTER_DISTANCE) { // Only move if not at exact center
        // Base attraction force (slower for resupplying)
        let attractX = (dx / dist) * unit.speed * 0.5;
        let attractY = (dy / dist) * unit.speed * 0.5;
        
        // Apply separation from nearby allies
        let separationX = 0;
        let separationY = 0;
        let separationCount = 0;
        
        for (const other of this.nearbyAllies) {
          if (!other || other === unit) continue;
          const otherDist = unit.getDistance(other);
          if (otherDist < MOVEMENT_CONFIG.ALLY_SEPARATION_RADIUS && otherDist > 0) { // Separation radius
            const sepDx = unit.x - other.x;
            const sepDy = unit.y - other.y;
            const sepDist = Math.sqrt(sepDx * sepDx + sepDy * sepDy);
            
            // Stronger separation when closer
            const strength = (MOVEMENT_CONFIG.ALLY_SEPARATION_RADIUS - otherDist) / MOVEMENT_CONFIG.ALLY_SEPARATION_RADIUS;
            separationX += (sepDx / sepDist) * strength;
            separationY += (sepDy / sepDist) * strength;
            separationCount++;
          }
        }
        
        // Average and scale separation
        if (separationCount > 0) {
          separationX = (separationX / separationCount) * unit.speed * MOVEMENT_CONFIG.ALLY_SEPARATION_FORCE;
          separationY = (separationY / separationCount) * unit.speed * MOVEMENT_CONFIG.ALLY_SEPARATION_FORCE;
        }
        
        // Combine forces
        unit.vx = attractX + separationX;
        unit.vy = attractY + separationY;
      } else {
        // Very close to center - minimal movement
        unit.vx *= MOVEMENT_CONFIG.CENTER_MOVEMENT_DECAY;
        unit.vy *= MOVEMENT_CONFIG.CENTER_MOVEMENT_DECAY;
      }
    } else {
      // Outside base - move toward base entrance
      unit.moveTowards(unit.base.x, unit.base.y, MOVEMENT_CONFIG.RETURN_RESOURCE_SPEED);
    }
    
    // Repair base while resupplying if it needs it
    if (unit.base.needsRepairers && unit.base.needsRepairers() && baseDistance <= BASE_CONFIG.REPAIR_RANGE) {
      // Register as repairer if not already
      if (!unit.isRepairingBase && unit.base.addRepairer(unit)) {
        unit.isRepairingBase = true;
        
        debugLog(unit.level, {
          action: 'resupplying_started_repair',
          unitId: unit.id,
          team: unit.team.name,
          baseHealth: Math.floor(unit.base.health),
          activeRepairers: unit.base.activeRepairers.size
        });
      }
    } else if (unit.isRepairingBase && unit.base) {
      // Base fully repaired or we're out of range - stop repairing
      unit.base.removeRepairer(unit);
      unit.isRepairingBase = false;
    }
    
    // Check if fully resupplied
    const healthReady = unit.health >= unit.maxHealth * THRESHOLD_CONFIG.HEALTH_READY_THRESHOLD;
    const courageReady = unit.courage >= unit.maxCourage * THRESHOLD_CONFIG.COURAGE_RESUPPLY_THRESHOLD;
    const ammoReady = unit.ammo >= unit.maxAmmo * THRESHOLD_CONFIG.AMMO_READY_THRESHOLD;
    
    if (healthReady && courageReady && ammoReady) {
      // Clean up repair status before leaving
      if (unit.isRepairingBase && unit.base) {
        unit.base.removeRepairer(unit);
        unit.isRepairingBase = false;
      }
      
      // All supplies are adequate - send check conditions event
      this.sendEvent(events.CHECK_CONDITIONS);
      
      debugLog(unit.level, {
        action: 'resupply_complete',
        unitId: unit.id,
        team: unit.team.name,
        health: Math.floor(unit.health),
        courage: Math.floor(unit.courage),
        ammo: unit.ammo
      });
    }
    
    // Check if base needs defense
    if (this.isBaseUnderAttack()) {
      this.sendEvent(events.BASE_PRIORITY_DEFEND);
    }
  }
  
  handleFleeingState() {
    const unit = this.unit;
    const now = Date.now();
    
    // Release nebula gathering slot if we were gathering
    this.releaseNebulaSlot();
    
    // If carrying metal, should be returning resource instead
    if (unit.carryingResource && unit.carryingResource.type === 'metal' && 
        unit.base && unit.base.alive) {
      debugLog(unit.level, {
        action: 'fleeing_with_resource',
        unitId: unit.id,
        resource: unit.carryingResource.type,
        redirecting: 'returning_resource'
      });
      this.sendEvent(events.RESOURCE_COLLECTED);
      return;
    }
    
    // Find nearby threats
    const threats = this.nearbyEnemies.filter(enemy => 
      unit.getDistance(enemy) < this.weaponRange * 2
    );
    
    // Opportunistic firing while fleeing
    if (unit.ammo > 0 && threats.length > 0) {
      const closestThreat = threats.reduce((closest, threat) => {
        const dist = unit.getDistance(threat);
        return (!closest || dist < unit.getDistance(closest)) ? threat : closest;
      }, null);
      
      if (closestThreat && unit.getDistance(closestThreat) <= this.weaponRange) {
        if (this.canFireAt(closestThreat)) {
          this.currentTarget = closestThreat;
          this.fireAt(closestThreat);
        }
      }
    }
    
    // Check for nearby corpses if health is low (opportunistic healing)
    if (unit.health < unit.maxHealth * THRESHOLD_CONFIG.HEALTH_CORPSE_SEEK_THRESHOLD) {
      const nearbyCorpse = this.findNearestCorpse();
      if (nearbyCorpse && unit.getDistance(nearbyCorpse) < AI_CONFIG.CORPSE_COLLECTION_RADIUS * THRESHOLD_CONFIG.CORPSE_FLEE_COLLECTION_RANGE) {
        // If corpse is close and no immediate threats, go for it
        if (threats.length === 0 || unit.getDistance(threats[0]) > this.weaponRange) {
          unit.moveTowardsWithBaseAvoidance(nearbyCorpse.x, nearbyCorpse.y, unit.allBases || []);
          return;
        }
      }
    }
    
    // Main fleeing behavior
    if (unit.base && unit.base.alive) {
      // Use smart base avoidance when returning to base
      this.handleFleeingMovement();
      
      // Check if we've reached base
      if (unit.base.isAtCenter && unit.base.isAtCenter(unit)) {
        unit.vx = 0;
        unit.vy = 0;
        
        debugLog(unit.level, {
          action: 'fleeing_reached_base',
          unitId: unit.id,
          team: unit.team.name,
          health: Math.floor(unit.health),
          courage: Math.floor(unit.courage),
          ammo: unit.ammo,
          unitState: unit.state,
          stateMachineState: this.stateService.machine.current
        });
        
        // Ensure state machine is in fleeing state
        if (this.stateService.machine.current !== 'fleeing') {
          const issueKey = `fleeing_mismatch_${unit.id}`;
          if (this.lastSafetyCheckIssue !== issueKey || now - this.lastSafetyCheckFrame > 1000) {
            debugWarn(unit.level, 'State machine not in fleeing state when sending REACHED_BASE', {
              unitId: unit.id,
              unitState: unit.state,
              stateMachineState: this.stateService.machine.current
            });
            this.lastSafetyCheckIssue = issueKey;
            this.lastSafetyCheckFrame = now;
          }
        }
        
        // Send REACHED_BASE event to trigger state transition
        this.sendEvent(events.REACHED_BASE);
      }
    } else if (threats.length > 0) {
      // No base - flee from threats
      this.fleeFromThreats(threats);
    } else {
      // No base and no threats - check courage
      if (unit.courage >= unit.maxCourage * 0.8) {
        // Courage restored - switch to final hunt
        this.sendEvent(events.CHECK_CONDITIONS);
      } else {
        // Wander while recovering courage
        this.wanderWhileRecovering();
      }
    }
  }
  
  handleCollectingCorpseState() {
    const unit = this.unit;
    
    // Don't collect corpses while carrying resources
    if (unit.carryingResource) {
      debugWarn(unit.level, 'Unit trying to collect corpse while carrying resource!', {
        unitId: unit.id,
        team: unit.team.name,
        resource: unit.carryingResource?.type || 'unknown',
        resourceObj: unit.carryingResource
      });
      this.sendEvent(events.RESOURCE_COLLECTED);
      return;
    }
    
    // Find the nearest corpse
    const targetCorpse = this.findNearestCorpse();
    
    if (!targetCorpse || !targetCorpse.active) {
      // No corpse found - return to hunting
      debugLog(unit.level, {
        action: 'collecting_corpse_no_target',
        unitId: unit.id,
        team: unit.team.name,
        reason: 'No active corpse found, returning to hunting'
      });
      this.sendEvent(events.CHECK_CONDITIONS);
      return;
    }
    
    const distance = unit.getDistance(targetCorpse);
    
    // Safety check - if corpse is too far, wander toward it
    if (distance > unit.perceptionRange) {
      debugLog(unit.level, {
        action: 'collecting_corpse_too_far_wandering',
        unitId: unit.id,
        team: unit.team.name,
        distance: distance.toFixed(1)
      });
      // Move toward the distant corpse (corpses are high priority) with base avoidance
      unit.moveTowardsWithBaseAvoidance(targetCorpse.x, targetCorpse.y, unit.allBases || [], 0.5);
      return;
    }
    
    // Check if we can collect it
    if (targetCorpse.canBeCollectedBy && targetCorpse.canBeCollectedBy(unit)) {
      // Collect immediately (corpses give instant level-up)
      const result = targetCorpse.collect(unit);
      targetCorpse.active = false;
      
      debugLog(unit.level, {
        action: 'collected_corpse',
        unitId: unit.id,
        team: unit.team.name,
        newLevel: unit.level
      });
      
      // Send collected event to state machine
      this.sendEvent(events.RESOURCE_COLLECTED);
    } else if (distance > AI_CONFIG.CORPSE_COLLECTION_RADIUS) {
      // Move directly to corpse with urgency and base avoidance
      unit.moveTowardsWithBaseAvoidance(targetCorpse.x, targetCorpse.y, unit.allBases || [], 1.2); // Move faster
    } else {
      // Can't collect and we're close - corpse might be taken, check for other options
      debugLog(unit.level, {
        action: 'collecting_corpse_cant_collect',
        unitId: unit.id,
        team: unit.team.name,
        distance: distance.toFixed(1),
        reason: 'Corpse taken or not collectable, checking conditions'
      });
      this.sendEvent(events.CHECK_CONDITIONS);
    }
  }
  
  handleFinalHuntState() {
    // Aggressive hunting when no base
    const target = this.selectBestTarget();
    if (target) {
      this.currentTarget = target;
      this.sendEvent(events.ENEMY_FOUND);
    } else {
      this.wander(MOVEMENT_CONFIG.FLEE_SPEED_MULTIPLIER); // Wander faster
    }
  }
  
  // Shared fleeing movement used by both fleeing and reloading states
  handleFleeingMovement() {
    const unit = this.unit;
    
    // If fleeing from a specific base (hit by missile), move away from it
    if (unit.fleeFromBase && unit.fleeFromBase.alive) {
      const fleeDistance = unit.fleeFromBase.weaponRange * 1.5;
      const currentDistance = unit.getDistance(unit.fleeFromBase);
      
      if (currentDistance < fleeDistance) {
        // Move directly away from the attacking base
        const dx = unit.x - unit.fleeFromBase.x;
        const dy = unit.y - unit.fleeFromBase.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 0) {
          const fleeSpeed = unit.speed * MOVEMENT_CONFIG.URGENT_SPEED_MULTIPLIER; // Move faster when fleeing
          unit.vx = (dx / distance) * fleeSpeed;
          unit.vy = (dy / distance) * fleeSpeed;
        }
        return;
      } else {
        // Far enough away, clear the flee target
        unit.fleeFromBase = null;
      }
    }
    
    // Normal fleeing/reloading - return to base
    if (unit.base && unit.base.alive) {
      // If very close to base, push toward center with boids separation
      const baseDistance = unit.getDistance(unit.base);
      if (baseDistance < unit.base.size) {
        // Inside base radius - apply boids-style movement toward center
        const dx = unit.base.x - unit.x;
        const dy = unit.base.y - unit.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist > THRESHOLD_CONFIG.BASE_CENTER_DISTANCE) { // Only move if not at exact center
          // Base attraction force
          let attractX = (dx / dist) * unit.speed * 0.8;
          let attractY = (dy / dist) * unit.speed * 0.8;
          
          // Apply separation from nearby allies
          let separationX = 0;
          let separationY = 0;
          let separationCount = 0;
          
          for (const other of this.nearbyAllies) {
            if (!other || other === unit) continue;
            const otherDist = unit.getDistance(other);
            if (otherDist < MOVEMENT_CONFIG.ALLY_SEPARATION_RADIUS && otherDist > 0) { // Separation radius
              const sepDx = unit.x - other.x;
              const sepDy = unit.y - other.y;
              const sepDist = Math.sqrt(sepDx * sepDx + sepDy * sepDy);
              
              // Stronger separation when closer
              const strength = (MOVEMENT_CONFIG.ALLY_SEPARATION_RADIUS - otherDist) / MOVEMENT_CONFIG.ALLY_SEPARATION_RADIUS;
              separationX += (sepDx / sepDist) * strength;
              separationY += (sepDy / sepDist) * strength;
              separationCount++;
            }
          }
          
          // Average and scale separation
          if (separationCount > 0) {
            separationX = (separationX / separationCount) * unit.speed * MOVEMENT_CONFIG.ALLY_SEPARATION_FORCE;
            separationY = (separationY / separationCount) * unit.speed * MOVEMENT_CONFIG.ALLY_SEPARATION_FORCE;
          }
          
          // Combine forces
          unit.vx = attractX + separationX;
          unit.vy = attractY + separationY;
        }
      } else {
        // Outside base - use smart base avoidance to approach (20% faster when fleeing)
        unit.moveTowardsWithBaseAvoidance(unit.base.x, unit.base.y, unit.allBases || [], MOVEMENT_CONFIG.URGENT_SPEED_MULTIPLIER);
      }
      
      // Can still fire at nearby enemies while fleeing
      if (unit.ammo > 0 && this.nearbyEnemies.length > 0) {
        const nearestEnemy = this.nearbyEnemies[0];
        const distance = unit.getDistance(nearestEnemy);
        if (distance <= this.weaponRange) {
          this.currentTarget = nearestEnemy;
          if (this.canFireAt(nearestEnemy)) {
            this.fireAt(nearestEnemy);
          }
        }
      }
    } else if (this.nearbyEnemies.length > 0) {
      // No base - flee from threats (50% faster when fleeing from enemies)
      const safeDir = this.findSafestDirection();
      const targetX = unit.x + Math.cos(safeDir) * MOVEMENT_CONFIG.SAFE_DIRECTION_DISTANCE;
      const targetY = unit.y + Math.sin(safeDir) * MOVEMENT_CONFIG.SAFE_DIRECTION_DISTANCE;
      unit.moveTowards(targetX, targetY, MOVEMENT_CONFIG.FLEE_SPEED_MULTIPLIER);
    } else {
      // No threats and no base - wander
      this.wander();
    }
  }
  
  // Combat methods
  selectBestTarget() {
    let bestTarget = null;
    let bestScore = -Infinity;
    
    // Target enemy soldiers first
    for (const enemy of this.nearbyEnemies) {
      if (!this.isValidTarget(enemy)) continue;
      
      const distance = this.unit.getDistance(enemy);
      if (distance > this.weaponRange * 1.5) continue; // Too far
      
      // Score based on threat level, distance, and health
      const threatLevel = this.threats.get(enemy) || 0;
      const healthRatio = enemy.health / enemy.maxHealth;
      const distanceScore = 1 - (distance / this.weaponRange);
      
      const score = threatLevel + distanceScore * 50 - healthRatio * 20;
      
      if (score > bestScore) {
        bestScore = score;
        bestTarget = enemy;
      }
    }
    
    // Level 10+ units can target enemy bases
    if (this.unit.level >= AI_CONFIG.BASE_TARGETING_MIN_LEVEL) {
      for (const base of this.nearbyBases) {
        if (!base.alive || base.team.id === this.unit.team.id) continue;
        
        const distance = this.unit.getDistance(base);
        const baseTargetRange = this.weaponRange * AI_CONFIG.BASE_TARGET_RANGE_MULTIPLIER;
        if (distance > baseTargetRange) continue; // Too far for base targeting
        
        // Bases are high-value targets but lower priority than soldiers
        const healthRatio = base.health / base.maxHealth;
        const distanceScore = 1 - (distance / baseTargetRange);
        const baseScore = distanceScore * 30 - healthRatio * 10; // Lower score than soldiers
        
        if (baseScore > bestScore) {
          bestScore = baseScore;
          bestTarget = base;
        }
      }
    }
    
    // Store target on unit for debug rendering compatibility
    if (bestTarget !== this.currentTarget) {
      this.unit.target = bestTarget;
    }
    
    return bestTarget;
  }
  
  canFireAt(target) {
    const unit = this.unit;
    const now = Date.now();
    
    
    if (unit.ammo <= 0) return false;
    if (now - this.lastFireTime < this.fireRate) return false;
    if (!this.isValidTarget(target)) return false;
    
    const distance = unit.getDistance(target);
    return distance <= this.weaponRange;
  }
  
  fireAt(target) {
    const unit = this.unit;
    const now = Date.now();
    
    // Predict target position
    const prediction = this.predictTargetPosition(target);
    
    // Add some random spread like original (skill-based accuracy)
    const spread = Math.max(COMBAT_CONFIG.WEAPON_SPREAD_MIN, (COMBAT_CONFIG.MAX_SKILL_CAP - unit.skill) / COMBAT_CONFIG.SKILL_TO_SPREAD_DIVISOR);
    const randomX = (Math.random() - 0.5) * spread * COMBAT_CONFIG.SPREAD_MULTIPLIER;
    const randomY = (Math.random() - 0.5) * spread * COMBAT_CONFIG.SPREAD_MULTIPLIER;
    
    // Fire from edge of soldier towards predicted position (like original)
    const dx = target.x - unit.x;
    const dy = target.y - unit.y;
    const angle = Math.atan2(dy, dx);
    const startX = unit.x + Math.cos(angle) * (unit.size + COMBAT_CONFIG.PROJECTILE_START_OFFSET);
    const startY = unit.y + Math.sin(angle) * (unit.size + COMBAT_CONFIG.PROJECTILE_START_OFFSET);
    
    // Create projectile
    const projectile = new Projectile(
      startX,
      startY,
      prediction.x + randomX,
      prediction.y + randomY,
      unit.damage,
      unit.color,
      unit,
      unit.canvas,
      target
    );
    
    // Add projectile to main game projectiles array
    if (unit.projectiles) {
      unit.projectiles.push(projectile);
    } else {
      debugError(`ERROR: unit.projectiles is null/undefined for Unit ${unit.id}`);
    }
    
    unit.ammo--;
    this.lastFireTime = now;
    unit.muzzleFlash = 1;
    
    // Play sound
    if (unit.audioManager) {
      unit.audioManager.playFire();
    }
    
    // Debug logging like original
    if (isDebugMode()) {
      const distance = unit.getDistance(target);
      const targetVx = this.targetInitialized ? (target.x - this.targetPrevX) : 0;
      const targetVy = this.targetInitialized ? (target.y - this.targetPrevY) : 0;
      
      debugLog(unit.level, {
        action: 'firing',
        targetId: target.id,
        targetPos: {x: target.x.toFixed(1), y: target.y.toFixed(1)},
        targetVel: {x: targetVx.toFixed(1), y: targetVy.toFixed(1)},
        predictedPos: {x: prediction.x.toFixed(1), y: prediction.y.toFixed(1)},
        distance: distance.toFixed(1),
        skill: unit.skill,
        targetState: target.state,
        targetHealth: target.health.toFixed(0)
      });
    }
  }
  
  predictTargetPosition(target) {
    const now = Date.now();
    
    // Initialize tracking
    if (!this.targetInitialized || this.currentTarget !== target) {
      this.targetPrevX = target.x;
      this.targetPrevY = target.y;
      this.targetPrevTime = now;
      this.targetInitialized = true;
      this.offScreenPredictionCount = 0;
      return { x: target.x, y: target.y };
    }
    
    // Calculate target's velocity in pixels per frame (frame-based like original)
    const deltaTime = now - this.targetPrevTime;
    const frameTime = THRESHOLD_CONFIG.MS_PER_FRAME; // 60 FPS
    const framesElapsed = Math.max(1, deltaTime / frameTime);
    
    // Check if target is a base (bases don't move)
    const targetIsBase = target.constructor.name === 'TeamBase';
    let targetVx = targetIsBase ? 0 : (target.x - this.targetPrevX) / framesElapsed;
    let targetVy = targetIsBase ? 0 : (target.y - this.targetPrevY) / framesElapsed;
    
    // Cap velocity to prevent wild predictions
    targetVx = clamp(targetVx, -AI_CONFIG.MAX_VELOCITY_CAP, AI_CONFIG.MAX_VELOCITY_CAP);
    targetVy = clamp(targetVy, -AI_CONFIG.MAX_VELOCITY_CAP, AI_CONFIG.MAX_VELOCITY_CAP);
    
    // Calculate distance to target
    const dx = target.x - this.unit.x;
    const dy = target.y - this.unit.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Calculate projectile speed (pixels per frame) - match original logic
    const projectileSpeed = PROJECTILE_CONFIG.BASE_SPEED + (this.unit.level - 1) * 0.5;
    const timeToTarget = distance / projectileSpeed; // Time in frames
    
    // Simple ballistic prediction with skill factor (like original)
    const skillFactor = this.unit.skill / COMBAT_CONFIG.MAX_SKILL_CAP; // 0.1 to 1.0 based on skill
    const predictionAccuracy = Math.min(skillFactor, COMBAT_CONFIG.MAX_PREDICTION_ACCURACY); // Cap at 85% accuracy
    
    // Calculate intercept point
    let predictedX = target.x + (targetVx * timeToTarget * predictionAccuracy);
    let predictedY = target.y + (targetVy * timeToTarget * predictionAccuracy);
    
    // Check if prediction would be off-screen
    const canvas = this.unit.canvas;
    const predictionOffScreen = predictedX < AI_CONFIG.PREDICTION_MARGIN || 
                               predictedX > canvas.width - AI_CONFIG.PREDICTION_MARGIN ||
                               predictedY < AI_CONFIG.PREDICTION_MARGIN || 
                               predictedY > canvas.height - AI_CONFIG.PREDICTION_MARGIN;
    
    // Handle off-screen predictions
    if (predictionOffScreen) {
      this.offScreenPredictionCount++;
      if (isDebugMode()) {
        debugLog(this.unit.level, `prediction off-screen (${this.offScreenPredictionCount}x), using direct aim`);
      }
      
      // Just aim directly at target without prediction
      predictedX = target.x;
      predictedY = target.y;
      
      // If too many off-screen predictions, abandon this target
      if (this.offScreenPredictionCount > AI_CONFIG.MAX_OFF_SCREEN_PREDICTIONS) {
        if (isDebugMode()) {
          debugLog(this.unit.level, 'abandoning target due to persistent off-screen predictions');
        }
        this.currentTarget = null;
        this.sendEvent(events.CHECK_CONDITIONS);
        this.offScreenPredictionCount = 0;
        return { x: target.x, y: target.y }; // Return direct aim before abandoning
      }
    } else {
      // Reset counter on good prediction
      this.offScreenPredictionCount = 0;
    }
    
    // Store for debug visualization (compatible with original Renderer)
    this.lastPredictedX = predictedX;
    this.lastPredictedY = predictedY;
    
    // Also store on unit for Renderer compatibility
    this.unit.lastPredictedX = predictedX;
    this.unit.lastPredictedY = predictedY;
    this.unit.target = target; // Store current target on unit for debug rendering
    
    // Update tracking for next prediction
    this.targetPrevX = target.x;
    this.targetPrevY = target.y;
    this.targetPrevTime = now;
    
    return { x: predictedX, y: predictedY };
  }
  
  // Guides the unit toward its preferred engagement distance without overshooting.
  // Once within the clamped combat band, combatStrafe() takes over to circle the target.
  moveToOptimalRange(target) {
    if (!target) {
      return;
    }

    const unit = this.unit;
    const minDistance = this.weaponRange * COMBAT_CONFIG.COMBAT_MIN_DISTANCE;
    const maxDistance = this.weaponRange * COMBAT_CONFIG.COMBAT_MAX_DISTANCE;
    const preferredMultiplier = COMBAT_CONFIG.COMBAT_IDEAL_DISTANCE ?? COMBAT_CONFIG.COMBAT_STRAFE_DISTANCE;
    const desiredDistance = clamp(
      this.weaponRange * preferredMultiplier,
      minDistance,
      maxDistance
    );

    const dx = unit.x - target.x;
    const dy = unit.y - target.y;
    const currentDistance = Math.sqrt(dx * dx + dy * dy);

    if (currentDistance < 1e-6) {
      const angle = this.combatAngle || Math.random() * Math.PI * 2;
      const targetX = target.x + Math.cos(angle) * desiredDistance;
      const targetY = target.y + Math.sin(angle) * desiredDistance;
      unit.moveTowardsWithBaseAvoidance(targetX, targetY, unit.allBases || []);
      return;
    }

    const targetX = target.x + (dx / currentDistance) * desiredDistance;
    const targetY = target.y + (dy / currentDistance) * desiredDistance;

    unit.moveTowardsWithBaseAvoidance(targetX, targetY, unit.allBases || []);
  }

  // Movement behaviors
  wander(speedMultiplier = 1) {
    // Use the Unit's smooth wander method
    this.unit.wander(speedMultiplier);
  }
  
  wanderSlightly() {
    // Small random movements to avoid stacking
    const unit = this.unit;
    const angle = Math.random() * Math.PI * 2;
    const distance = 5; // Small movement
    
    const targetX = unit.x + Math.cos(angle) * distance;
    const targetY = unit.y + Math.sin(angle) * distance;
    
    unit.moveTowards(targetX, targetY, MOVEMENT_CONFIG.WANDER_SPEED_MULTIPLIER * 0.3);
  }
  
  patrolAroundBase() {
    const unit = this.unit;
    const base = unit.base;
    
    if (!base) return;
    
    // Initialize patrol angle if not set
    if (this.patrolAngle === undefined) {
      this.patrolAngle = Math.random() * Math.PI * 2;
    }
    
    // Slowly rotate patrol angle for circular movement
    this.patrolAngle += MOVEMENT_CONFIG.PATROL_ANGLE_INCREMENT; // Adjust speed of patrol rotation
    
    // Calculate patrol position around base
    const patrolRadius = base.size * 2;
    const targetX = base.x + Math.cos(this.patrolAngle) * patrolRadius;
    const targetY = base.y + Math.sin(this.patrolAngle) * patrolRadius;
    
    // Move toward patrol position
    unit.moveTowards(targetX, targetY, MOVEMENT_CONFIG.PATROL_SPEED_MULTIPLIER); // Moderate patrol speed
  }
  
  combatStrafe() {
    const unit = this.unit;

    // Strafe around target
    this.combatAngle += MOVEMENT_CONFIG.COMBAT_ANGLE_INCREMENT;

    const radius = this.weaponRange * COMBAT_CONFIG.COMBAT_STRAFE_DISTANCE;
    const targetX = this.currentTarget.x + Math.cos(this.combatAngle) * radius;
    const targetY = this.currentTarget.y + Math.sin(this.combatAngle) * radius;

    // Use base-aware movement to avoid strafing into enemy base weapon range
    unit.moveTowardsWithBaseAvoidance(targetX, targetY, unit.allBases || [], MOVEMENT_CONFIG.PATROL_SPEED_MULTIPLIER);
  }
  
  findSafestDirection() {
    const unit = this.unit;
    let safestAngle = 0;
    let maxSafety = -Infinity;
    
    // If no threats, continue in current direction with some randomness
    if (this.threats.size === 0) {
      if (!this.retreatAngle) {
        // Initialize retreat angle based on current velocity or random
        if (Math.abs(unit.vx) > 0.1 || Math.abs(unit.vy) > 0.1) {
          this.retreatAngle = Math.atan2(unit.vy, unit.vx);
        } else {
          this.retreatAngle = Math.random() * Math.PI * 2;
        }
      }
      // Add some wander to avoid getting stuck
      this.retreatAngle += (Math.random() - 0.5) * 0.2;
      return this.retreatAngle;
    }
    
    // Check 8 directions
    for (let i = 0; i < MOVEMENT_CONFIG.SAFE_DIRECTION_CHECKS; i++) {
      const angle = (i / MOVEMENT_CONFIG.SAFE_DIRECTION_CHECKS) * Math.PI * 2;
      const checkX = unit.x + Math.cos(angle) * MOVEMENT_CONFIG.SAFE_DIRECTION_DISTANCE;
      const checkY = unit.y + Math.sin(angle) * MOVEMENT_CONFIG.SAFE_DIRECTION_DISTANCE;
      
      let safety = 0;
      
      // Calculate safety based on distance from threats
      for (const [entity, threatLevel] of this.threats) {
        const dx = checkX - entity.x;
        const dy = checkY - entity.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        safety += distance / (threatLevel + 1);
      }
      
      // Also consider distance from edges
      const canvasWidth = this.unit.canvas ? this.unit.canvas.width : CANVAS_CONFIG.DEFAULT_WIDTH;
      const canvasHeight = this.unit.canvas ? this.unit.canvas.height : CANVAS_CONFIG.DEFAULT_HEIGHT;
      const edgeDistance = Math.min(
        checkX,
        checkY,
        canvasWidth - checkX,
        canvasHeight - checkY
      );
      safety += edgeDistance * MOVEMENT_CONFIG.EDGE_SAFETY_WEIGHT; // Slight preference for staying away from edges
      
      if (safety > maxSafety) {
        maxSafety = safety;
        safestAngle = angle;
      }
    }
    
    // Store the angle for next time if no threats
    this.retreatAngle = safestAngle;
    return safestAngle;
  }
  
  // Utility methods
  findEnemyNearPosition(position, radius = 150, preferredId = null) {
    if (!position) return null;

    if (preferredId) {
      const preferred = this.nearbyEnemies?.find(enemy => enemy && enemy.alive && enemy.id === preferredId);
      if (preferred) {
        return preferred;
      }
    }

    if (!this.nearbyEnemies || this.nearbyEnemies.length === 0) return null;

    for (const enemy of this.nearbyEnemies) {
      if (!enemy || !enemy.alive) continue;
      if (this.unit.getDistance(enemy) <= radius) {
        return enemy;
      }
    }

    return null;
  }

  shouldRetreatFromCombat() {
    const unit = this.unit;
    const target = this.currentTarget;

    // Don't retreat too often
    if (this.retreatCooldown > 0) return false;
    
    // No target means no need to retreat
    if (!target || !target.alive) return false;
    
    // Don't retreat if we're much higher level (confident in our abilities)
    const levelDifference = target.level - unit.level;
    if (levelDifference <= 1) return false;
    
    // Retreat conditions based on tactical assessment (from original system)
    const healthRatio = unit.health / unit.maxHealth;
    const targetHealthRatio = target.health / target.maxHealth;
    
    // More likely to retreat if:
    // 1. Enemy is significantly higher level (2+ levels)
    // 2. We're at low health (<50%) and enemy is healthier
    // 3. Enemy is a berserker (very dangerous)
    
    let shouldRetreat = false;

    if (target.isBerserker) {
      // Always consider retreating from berserkers if not a berserker ourselves
      shouldRetreat = !unit.isBerserker && healthRatio < THRESHOLD_CONFIG.RETREAT_BERSERKER_HEALTH;
    } else if (levelDifference >= 3) {
      // Enemy is 3+ levels higher - high retreat chance
      shouldRetreat = healthRatio < THRESHOLD_CONFIG.RETREAT_LEVEL3_HEALTH;
    } else if (levelDifference >= THRESHOLD_CONFIG.RETREAT_LEVEL_DIFFERENCE) {
      // Enemy is 2 levels higher - retreat if we're damaged
      shouldRetreat = healthRatio < THRESHOLD_CONFIG.RETREAT_LEVEL2_HEALTH &&
        targetHealthRatio > THRESHOLD_CONFIG.RETREAT_TARGET_HEALTH;
    }

    // Radio System Integration: Call for help when retreating
    // Broadcasts assist request to team with calculated threat level
    // Threat calculation:
    // - Base 0.5 threat
    // - +0.15 per level difference (stronger enemy = more urgent)
    // - +0.2 based on enemy health ratio (healthy enemy = more dangerous)
    if (shouldRetreat && this.radioSystem && typeof this.radioSystem.requestAssist === 'function') {
      const threatLevel = clamp(
        Math.min(1,
          0.5 + Math.max(0, levelDifference) * 0.15 + targetHealthRatio * 0.2
        ),
        0,
        1
      );

      // Broadcast: "Help! I'm retreating from [enemy] at my position, threat level X"
      // Teammates will evaluate based on distance, their health/ammo, and busy state
      this.radioSystem.requestAssist({
        enemy: target,                    // Enemy we're fleeing from
        threatLevel,                      // How dangerous (0-1)
        reason: 'retreating_from_combat'  // Reason code for logging
      });
    }

    return shouldRetreat;
  }
  
  shouldGatherResource(resource) {
    const unit = this.unit;

    // Don't gather if low on health or ammo
    if (unit.health < unit.maxHealth * 0.5) return false;
    if (unit.ammo < unit.maxAmmo * 0.3) return false;

    // Don't gather if enemies nearby
    if (this.nearbyEnemies.length > 0) return false;

    // Don't "steal" resources being tractored by our own base
    if (unit.base && unit.base.tractorBeamResources) {
      if (unit.base.tractorBeamResources.includes(resource)) {
        return false; // Base is already pulling this in
      }
    }

    // CRITICAL: Don't gather resources within enemy base weapon range (suicide!)
    // Check ALL enemy bases (not just nearby ones) to see if resource is in their firing range
    const allBases = unit.allBases || [];
    for (const base of allBases) {
      // Skip friendly bases and dead bases
      if (base.team.id === unit.team.id || !base.alive) continue;

      const distanceToResource = Math.hypot(resource.x - base.x, resource.y - base.y);
      // Add 20px buffer beyond weapon range for safety
      if (distanceToResource < base.weaponRange + 20) {
        return false; // Too dangerous - enemy base can shoot us while we gather
      }
    }
    
    // Check resource type priorities
    if (resource instanceof Nebula && unit.base && unit.base.resources.nebula < 50) {
      return true;
    }
    
    // Crystal cores are always valuable - collect immediately
    if (resource instanceof Crystal) {
      return true;
    }
    
    // Metal gathering - opportunistic for low-level soldiers with cargo space
    if (resource instanceof Metal && unit.base) {
      // Level < 5 soldiers always collect when cargo has space
      if (unit.level < 5 && unit.getCargoCount() < BASE_CONFIG.SOLDIER_CARGO_CAPACITY) {
        return true;
      }

      // Level 5+ only collect if base critically needs ammo
      if (unit.level >= 5 && unit.base.ammo < unit.base.maxAmmo * 0.2) {
        return true;
      }

      return false;
    }
    
    return false;
  }
  
  isBaseUnderAttack() {
    const unit = this.unit;
    if (!unit.base || !unit.base.alive) return false;
    
    // Check if enemies are near base
    for (const enemy of this.nearbyEnemies) {
      const distToBase = getDistance(enemy, unit.base);
      if (distToBase < THRESHOLD_CONFIG.BASE_THREAT_DETECTION_RANGE) return true;
    }
    
    return false;
  }
  
  resetEngagement() {
    this.engagementStartTime = 0;
    this.engagementDamageDealt = 0;
    this.engagementDamageTaken = 0;
  }
  
  // Track damage for retreat decisions
  onDamageDealt(amount) {
    this.engagementDamageDealt += amount;
  }
  
  onDamageTaken(amount) {
    this.engagementDamageTaken += amount;
  }
  
  // Find nearest corpse for healing/level up
  findNearestCorpse() {
    const unit = this.unit;
    let nearestCorpse = null;
    let nearestDistance = Infinity;

    // Check all resources for corpses
    for (const resource of this.nearbyResources) {
      if (resource instanceof Corpse && resource.active) {
        // Don't "steal" corpses being tractored by our own base
        if (unit.base && unit.base.tractorBeamCorpses) {
          if (unit.base.tractorBeamCorpses.includes(resource)) {
            continue; // Base is already harvesting this corpse for organics
          }
        }

        // CRITICAL: Don't collect corpses within enemy base weapon range (suicide!)
        // Check ALL enemy bases (not just nearby ones)
        const allBases = unit.allBases || [];
        let isSafe = true;

        for (const base of allBases) {
          // Skip friendly bases and dead bases
          if (base.team.id === unit.team.id || !base.alive) continue;

          const distanceToCorpse = Math.hypot(resource.x - base.x, resource.y - base.y);
          // Add 20px buffer beyond weapon range for safety
          if (distanceToCorpse < base.weaponRange + 20) {
            isSafe = false; // Too dangerous - enemy base can shoot us while we loot
            break;
          }
        }

        if (!isSafe) continue; // Skip this corpse, it's in the danger zone

        const distance = unit.getDistance(resource);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestCorpse = resource;
        }
      }
    }

    return nearestCorpse;
  }
  
  // Find nearest nebula for organics gathering
  findNearestNebula() {
    const unit = this.unit;
    let nearestNebula = null;
    let nearestDistance = Infinity;
    
    // Check all resources for nebulas with available slots
    for (const resource of this.nearbyResources) {
      if (resource instanceof Nebula && resource.active) {
        // Skip nebulas that are at capacity
        if (resource.gatherers && resource.gatherers.size >= RESOURCE_CONFIG.NEBULA_MAX_GATHERERS) {
          continue;
        }
        
        const distance = unit.getDistance(resource);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestNebula = resource;
        }
      }
    }
    
    return nearestNebula;
  }
  
  // Find nearest resource (metal, corpse, etc)
  findNearestResource() {
    const unit = this.unit;
    let nearestResource = null;
    let nearestDistance = Infinity;
    
    // Prioritize corpses if low health
    if (unit.health < unit.maxHealth * THRESHOLD_CONFIG.HEALTH_CORPSE_SEEK_THRESHOLD) {
      const corpse = this.findNearestCorpse();
      if (corpse) return corpse;
    }
    
    // Find other resources
    for (const resource of this.nearbyResources) {
      if (!resource.active) continue;
      
      // Skip nebula/crystal if already being gathered
      if ((resource.type === 'nebula' || resource.type === 'crystal') && 
          resource.gatherers && resource.gatherers.size >= resource.maxGatherers) {
        continue;
      }
      
      // Check if we should gather this resource
      if (this.shouldGatherResource(resource)) {
        const distance = unit.getDistance(resource);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestResource = resource;
        }
      }
    }
    
    return nearestResource;
  }
  
  // Flee from multiple threats
  fleeFromThreats(threats) {
    const unit = this.unit;
    
    // Calculate average threat position
    let avgX = 0, avgY = 0;
    for (let threat of threats) {
      avgX += threat.x;
      avgY += threat.y;
    }
    avgX /= threats.length;
    avgY /= threats.length;
    
    // Run away from average threat position
    unit.moveAway(avgX, avgY, 1.5); // Move faster when fleeing
  }
  
  // Wander while recovering courage
  wanderWhileRecovering() {
    const unit = this.unit;
    
    // Wander at reduced speed while recovering
    this.wander(MOVEMENT_CONFIG.PATROL_SPEED_MULTIPLIER);

    // Look for safe resources (corpses) while wandering
    const nearbyCorpse = this.findNearestCorpse();
    if (nearbyCorpse && unit.getDistance(nearbyCorpse) < unit.perceptionRange) {
      unit.moveTowardsWithBaseAvoidance(nearbyCorpse.x, nearbyCorpse.y, unit.allBases || []);
    }
  }
  
  logResourceGathering(resourceType, amount) {
    // Log soul resource gathering event if this soldier has a soul
    if (this.unit.incarnationId) {
      // Import at runtime to avoid circular dependency
      import('../systems/SoulforgeAPI.js').then(({ soulforgeAPI }) => {
        soulforgeAPI.logResourceGathered(this.unit.id, resourceType, amount);
      });
    }
  }
}